<script>
import { explorerState } from "@app/store/explorerStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import {
    eventGroupState,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import {
    projectState,
} from "@app/store/projectStore.svelte.js";
import {
    modalState,
    showModal,
    hideModal,
} from "@app/store/modalStore.svelte.js";
import { editorState, filterState } from "@app/store/editorStore.svelte.js";
import { setSelectionAnchor } from "@app/lib/eventSelection.js";
import {
    speakerState,
} from "@app/store/speakerStore.svelte.js";
import {
    markerState,
} from "@app/store/markerStore.svelte.js";
import { issueState } from "@app/store/issueStore.svelte.js";
import {
    metadataState,
} from "@app/store/metadataStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import {
    playerState,
    updatePlayerSpeed,
} from "@app/store/playerStore.svelte.js";
import {
    togglePlay,
    toggleFastForward,
    toggleRewind,
} from "@app/lib/pedal/transportControls.js";
import {
    insertHistoryAction,
    commitHistory,
    establishHistoryBaseline,
    undoHistory,
    redoHistory,
    isUndoBlockedByLock,
    isRedoBlockedByLock,
} from "@app/store/historyStore.svelte.js";
import { current } from "immer";
import { environment } from "@app/store/envStore.svelte.js";
import { uiState, toggleAssistant } from "@app/store/uiStore.svelte.js";
import { canEditMetadata } from "@app/store/permissionStore.svelte.js";
import { onMount } from "svelte";
import { fade } from "svelte/transition";

import _Event from "@cclib/classes/event.js";
import { getFilteredEventIndices } from "@app/lib/eventValidation.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Marker from "@cclib/classes/marker.js";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";
import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import splitQuillHtml from "@cclib/functions/quill/splitQuillHtml.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import getAvgTimePerWord from "@cclib/functions/events/getAvgTimePerWord.js";
import shiftWordBack from "@cclib/functions/quill/shiftWordBack.js";
import shiftWordForward from "@cclib/functions/quill/shiftWordForward.js";
import shiftLineBack from "@cclib/functions/quill/shiftLineBack.js";
import shiftLineForward from "@cclib/functions/quill/shiftLineForward.js";
import compressText from "@cclib/functions/quill/compressText.js";
import expandText from "@cclib/functions/quill/expandText.js";
import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import colorLookup from "@cclib/dict/colors.js";

import { ccprjFunc } from "@app/lib/ccprj.js";
import {
    normalizeEventXPosition,
    normalizeEventYPosition,
    resolveEventXPosition,
    resolveEventYPosition,
} from "@app/lib/eventPosition.js";
import {
    snapSelectedEndsToNext,
    snapSelectedStartsToPrevious,
} from "@app/lib/snapEvents.js";
import { audioScrub } from "@app/store/audioScrubStore.svelte.js";

/* Firebase */
import { auth } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import storage from "@app/configs/storage.js";
import { collection, doc, updateDoc, Timestamp } from "firebase/firestore";
import { ref, uploadString } from "firebase/storage";

import {
    KEYBOARD_SHORTCUTS_UPDATED_EVENT,
    getStoredKeyboardShortcuts,
    normalizeKeyboardShortcuts,
    saveKeyboardShortcuts,
} from "@app/lib/keyboardShortcuts.js";

import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { getCopyText, getHighlightedDomText, getQuillTextSelection } from "@app/lib/copySelection.js";
import * as m from "@app/paraglide/messages.js";

let selectedShortcut = $state({}),
    userId,
    teamId,
    teamRef,
    homeRef,
    storageRef,
    teamDriveUploadBasePath,
    myDriveUploadBasePath,
    playbackSpeeds = [
        0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 4.0, 8.0, 16.0,
    ],
    files = $state();

if (environment.online) {
    (userId = auth.currentUser.uid),
        (teamId = authState.team ? authState.team.id : null),
        (homeRef = collection(db, "users", userId, "storage")),
        (teamRef = teamId
            ? collection(db, "teams", teamId, "storage")
            : null),
        (storageRef = ref(storage)),
        (teamDriveUploadBasePath = "teams/" + teamId + "/storage/"),
        (myDriveUploadBasePath = "users/" + userId + "/projects/");
}

function selectEditorEvent(eventIndex) {
    const eventGroup = eventGroupState[projectState.selected];

    if (!eventGroup || !eventGroup.events || eventGroup.events.length === 0) {
        return false;
    }

    const selectedIndex = Math.min(
        Math.max(eventIndex, 0),
        eventGroup.events.length - 1,
    );
    const selectedEvent = eventGroup.events[selectedIndex];

    if (!selectedEvent) {
        return false;
    }

    eventGroup.selected = [selectedIndex];
    setSelectionAnchor(eventGroup.id, selectedIndex);

    if (lockState.video) {
        player.currentTime = selectedEvent.start;
    }

    window.dispatchEvent(
        new CustomEvent("scrollToVirtualListItem", {
            detail: { index: selectedIndex },
        }),
    );

    return true;
}

function justifySelectedEvents(alignment, xPos) {
    const eventGroup = eventGroupState[projectState.selected];

    if (!eventGroup || !eventGroup.selected || eventGroup.selected.length === 0) {
        return false;
    }

    let updated = false;

    eventGroup.selected.forEach((eventIndex) => {
        const selectedEvent = eventGroup.events?.[eventIndex];

        if (selectedEvent) {
            selectedEvent.alignment = alignment;
            selectedEvent.xPos = xPos;
            updated = true;
        }
    });

    if (!updated) {
        return false;
    }

    insertHistoryAction({
        name: "position event(s)", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });

    return true;
}

function alignSelectedEvents(alignment) {
    const eventGroup = eventGroupState[projectState.selected];

    if (!eventGroup || !eventGroup.selected || eventGroup.selected.length === 0) {
        return false;
    }

    let updated = false;

    eventGroup.selected.forEach((eventIndex) => {
        const selectedEvent = eventGroup.events?.[eventIndex];

        if (selectedEvent) {
            selectedEvent.alignment = alignment;
            updated = true;
        }
    });

    if (!updated) {
        return false;
    }

    insertHistoryAction({
        name: "align event(s)", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });

    return true;
}

function applySelectedTextColor(colorName) {
    const quill = window.quillEditor;

    if (!quill) {
        return false;
    }

    const color = colorLookup.toWebColor(colorName);
    const range = quill.getSelection();

    if (!range) {
        return false;
    }

    if (range.length > 0) {
        quill.formatText(range.index, range.length, "color", color);
    } else {
        const [line, offset] = quill.getLine(range.index) || [];

        if (!line) {
            return false;
        }

        const lineStart = range.index - offset;
        const lineLength = Math.max(line.length() - 1, 0);

        if (lineLength > 0) {
            quill.formatText(lineStart, lineLength, "color", color);
        } else {
            quill.format("color", color);
        }
    }

    insertHistoryAction({
        name: "update text",
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });

    return true;
}

function applySelectedEventTrack(track) {
    const eventGroup = eventGroupState[projectState.selected];

    if (
        !eventGroup ||
        eventGroup.type === "multiview" ||
        !eventGroup.selected?.length
    ) {
        return false;
    }

    eventGroup.selected.forEach((eventIndex) => {
        const selectedEvent = eventGroup.events?.[eventIndex];

        if (selectedEvent) {
            selectedEvent.track = track;
        }
    });

    insertHistoryAction({
        name: "update event track",
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });

    return true;
}

let shortcutGroups = $state([
    {
        name: "System",
        shortcuts: [
            {
                name: "New Project",
                description: "Open the New Project window",
                keyCmd: "",

                action: async function () {
                    showModal("newProject");
                },
            },

            {
                name: "Export File",
                description: "Open the export menu",
                keyCmd: "",

                action: async function () {
                    showModal("fileExport");
                },
            },
            {
                name: "Batch Export",
                description: "Open the batch export menu",
                keyCmd: "",

                action: async function () {
                    showModal("batchSubtitleExport");
                },
            },
            {
                name: "Import File",
                description: "Open the import file menu",
                keyCmd: "",

                action: async function () {
                    showModal("fileImport");
                },
            },
            {
                name: "Toggle AI Assistant",
                description: "Open or close the AI Assistant panel",
                keyCmd: "",

                action: async function () {
                    if (!environment.online) {
                        return;
                    }
                    toggleAssistant();
                },
            },
            {
                name: "Save Project",
                description: "Save project or open 'Save As' dialogue box",
                keyCmd: "",

                action: async function () {
                    if (environment.iframe) {
                        return;
                    }

                    if (projectState.useLocalStorage || !environment.online) {
                        try {
                            // Show immediate feedback for local save
                            toast.push(m.shortcut_keys_saving_project_toast(), {
                                classes: ["toast-info"],
                            });

                            if (environment.electron) {
                                const fs = window.fs;
                                const ipcRenderer = window.ipcRenderer;
                                if (projectState.localPath) {
                                    let projectJson = ccprjFunc.createCcprj({
                                        project: JSON.parse(
                                            JSON.stringify(projectState),
                                        ),
                                        metadata: JSON.parse(
                                            JSON.stringify(metadataState),
                                        ),
                                        speakers: JSON.parse(
                                            JSON.stringify(speakerState),
                                        ),
                                        issues: JSON.parse(
                                            JSON.stringify(issueState),
                                        ),
                                        markers: JSON.parse(
                                            JSON.stringify(markerState),
                                        ),
                                        eventGroups: JSON.parse(
                                            JSON.stringify(eventGroupState),
                                        ),
                                        style: JSON.parse(
                                            JSON.stringify(styleState),
                                        ),
                                    });
                                    
                                    await fs.writeFile(
                                        projectState.localPath,
                                        JSON.stringify(projectJson, null, 4),
                                    );

                                    toast.push(
                                        m.shortcut_keys_save_project_success_toast(),
                                        {
                                            classes: ["toast-success"],
                                        },
                                    );
                                } else {
                                    let filePath = await ipcRenderer.invoke(
                                        "openSaveDialog",
                                        {
                                            title: m.shortcut_keys_save_project_dialog_title(),
                                            defaultPath: `${projectState.name}.ccprj`,
                                            properties: [
                                                "showOverwriteConfirmation",
                                                "createDirectory",
                                            ],
                                        },
                                    );

                                    if (filePath.canceled) {
                                        throw new Error(
                                            m.shortcut_keys_save_project_cancelled_error(),
                                        );
                                    }

                                    projectState.localPath = filePath.filePath;
                                    projectState.name = filePath.filePath
                                        .replace(/^.*[\\\/]/, "")
                                        .replace(/\.ccprj/g, "");

                                    let projectJson = ccprjFunc.createCcprj({
                                        project: JSON.parse(
                                            JSON.stringify(projectState),
                                        ),
                                        metadata: JSON.parse(
                                            JSON.stringify(metadataState),
                                        ),
                                        speakers: JSON.parse(
                                            JSON.stringify(speakerState),
                                        ),
                                        issues: JSON.parse(
                                            JSON.stringify(issueState),
                                        ),
                                        markers: JSON.parse(
                                            JSON.stringify(markerState),
                                        ),
                                        eventGroups: JSON.parse(
                                            JSON.stringify(eventGroupState),
                                        ),
                                        style: JSON.parse(
                                            JSON.stringify(styleState),
                                        ),
                                    });

                                    await fs.writeFile(
                                        projectState.localPath,
                                        JSON.stringify(projectJson, null, 4),
                                    );

                                    toast.push(
                                        m.shortcut_keys_save_project_success_toast(),
                                        {
                                            classes: ["toast-success"],
                                        },
                                    );
                                }

                                establishHistoryBaseline({ name: "save project" });
                            } else {
                                let projectJson = ccprjFunc.createCcprj({
                                    project: JSON.parse(
                                        JSON.stringify(projectState),
                                    ),
                                    metadata: JSON.parse(
                                        JSON.stringify(metadataState),
                                    ),
                                    speakers: JSON.parse(
                                        JSON.stringify(speakerState),
                                    ),
                                    issues: JSON.parse(
                                        JSON.stringify(issueState),
                                    ),
                                    markers: JSON.parse(
                                        JSON.stringify(markerState),
                                    ),
                                    eventGroups: JSON.parse(
                                        JSON.stringify(eventGroupState),
                                    ),
                                    style: JSON.parse(
                                        JSON.stringify(styleState),
                                    ),
                                });

                                let fileBlob = new Blob(
                                    [JSON.stringify(projectJson, null, 4)],
                                    {
                                        type: "text/plain;charset=utf-8",
                                    },
                                );

                                saveAs(fileBlob, projectState.name + ".ccprj", {
                                    autoBom: true,
                                });

                                establishHistoryBaseline({ name: "save project" });
                            }
                        } catch (err) {
                            console.log(err, err.message);
                            toast.push(
                                m.shortcut_keys_save_project_error_toast({
                                    error: err.message,
                                }),
                                {
                                    classes: ["toast-danger"],
                                },
                            );
                        }
                    } else {
                        if (
                            !projectState.folderId &&
                            projectState.folderId !== null
                        ) {
                            explorerState.context = "save";
                            showModal("storageExplorer");
                        } else {
                            if (projectState.rootDir === "team" && !teamId) {
                                teamId = authState.team
                                    ? authState.team.id
                                    : null;
                                teamRef = teamId
                                    ? collection(db, "teams", teamId, "storage")
                                    : null;
                                teamDriveUploadBasePath =
                                    "teams/" + teamId + "/storage/";
                            }

                            let projectJson = ccprjFunc.createCcprj({
                                project: JSON.parse(
                                    JSON.stringify(projectState),
                                ),
                                metadata: JSON.parse(
                                    JSON.stringify(metadataState),
                                ),
                                speakers: JSON.parse(
                                    JSON.stringify(speakerState),
                                ),
                                issues: JSON.parse(JSON.stringify(issueState)),
                                markers: JSON.parse(
                                    JSON.stringify(markerState),
                                ),
                                eventGroups: JSON.parse(
                                    JSON.stringify(eventGroupState),
                                ),
                                style: JSON.parse(JSON.stringify(styleState)),
                            });

                            let projectFilePath =
                                projectState.rootDir === "team"
                                    ? teamDriveUploadBasePath + projectState.id
                                    : myDriveUploadBasePath + projectState.id;

                            uploadString(
                                ref(storageRef, projectFilePath),
                                JSON.stringify(projectJson),
                            )
                                .then((snapshot) => {
                                    //console.log("project file saved!", snapshot);
                                    let projectInfo = {
                                        id: projectState.id,
                                        name: projectState.name,
                                        type:
                                            projectState.type === "team"
                                                ? "team project"
                                                : "project",
                                        updatedOn:
                                            Timestamp.fromDate(
                                                new Date(),
                                            ),
                                        shared:
                                            projectState.rootDir === "team"
                                                ? true
                                                : false,
                                        locationId: projectState.folderId,
                                    };

                                    if (projectState.rootDir === "team") {
                                        return updateDoc(
                                            doc(teamRef, projectInfo.id),
                                            projectInfo,
                                        );
                                    } else {
                                        return updateDoc(
                                            doc(homeRef, projectInfo.id),
                                            projectInfo,
                                        );
                                    }
                                })
                                .then(() => {
                                    toast.push(m.shortcut_keys_project_saved_success_toast(), {
                                        classes: ["toast-success"],
                                    });
                                })
                                .catch((error) => {
                                    console.log(
                                        "Error saving project: ",
                                        error,
                                    );
                                    toast.push(error.message, {
                                        classes: ["toast-danger"],
                                    });
                                });
                            establishHistoryBaseline({ name: "save project" });
                        }
                    }
                },
            },
            {
                name: "Copy",
                description: "Copy event or selected text",
                keyCmd: "",

                action: async function () {
                    if (isInputFocused()) {
                        console.log("input focused");
                        let selectedText = "";
                        //copy selected text from active input field
                        let activeElement = document.activeElement;
                        //Get selected text from active element
                        const start = activeElement.selectionStart;
                        const end = activeElement.selectionEnd;
                        if (
                            start !== undefined &&
                            end !== undefined &&
                            start !== end
                        ) {
                            selectedText = activeElement.value.substring(
                                start,
                                end,
                            );
                            navigator.clipboard.writeText(selectedText);
                        }
                    } else {
                        const textToCopy = getCopyText({
                            eventGroup: eventGroupState[projectState.selected],
                        });
                        if (textToCopy != null) {
                            navigator.clipboard.writeText(textToCopy);
                        }
                    }
                },
            },
            {
                name: "Cut",
                description: "Cut event or selected text",
                keyCmd: "",

                action: function () {
                    if (isInputFocused()) {
                        let findFormInput =
                            document.getElementById("FindInput");
                        let replaceFormInput =
                            document.getElementById("ReplaceInput");
                        let activeElement = document.activeElement;

                        if (activeElement === findFormInput) {
                            navigator.clipboard.writeText(findFormInput.value);
                            findFormInput.value = "";
                        } else if (activeElement === replaceFormInput) {
                            navigator.clipboard.writeText(
                                replaceFormInput.value,
                            );
                            replaceFormInput.value = "";
                        } else if (activeElement.id === "NotesTextArea") {
                            navigator.clipboard.writeText(
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ].notes,
                            );
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].notes = "";
                        } else if (
                            activeElement.id === "ReplyTextArea" &&
                            eventGroupState[projectState.selected]?.events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ]
                        ) {
                            navigator.clipboard.writeText(
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ].reply,
                            );
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].reply = "";
                        } else if (
                            activeElement.id === "textareanotesinputlabel"
                        ) {
                            navigator.clipboard.writeText(
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ].notes,
                            );
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].notes = "";
                        } else if (
                            activeElement.id === "textareareplyinputlabel" &&
                            eventGroupState[projectState.selected]?.events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ]
                        ) {
                            navigator.clipboard.writeText(
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ].reply,
                            );
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].reply = "";
                        }
                    } else if (
                        eventGroupState[projectState.selected].selected.length >
                        0
                    ) {
                        const eventGroup =
                            eventGroupState[projectState.selected];
                        const quillSelection = getQuillTextSelection(eventGroup);
                        if (quillSelection) {
                            quillSelection.editor.deleteText(
                                quillSelection.range.index,
                                quillSelection.range.length,
                            );
                            navigator.clipboard.writeText(quillSelection.text);
                            insertHistoryAction({
                                name: "cut event(s)", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                            return;
                        }

                        const domText = getHighlightedDomText();
                        if (domText) {
                            navigator.clipboard.writeText(domText);
                            return;
                        }

                        let eventsToCut = [];
                        let selectedEvents = eventGroup.selected.map(
                            (selectedEvent) => {
                                return selectedEvent;
                            },
                        );

                        eventGroupState[projectState.selected].selected = [];

                        selectedEvents
                            .sort((a, b) => {
                                return b - a;
                            })
                            .forEach((eventIndex) => {
                                eventsToCut.push(
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex],
                                );
                                eventGroupState[projectState.selected] =
                                    removeEvent(
                                        eventGroupState[projectState.selected],
                                        eventIndex,
                                    );
                            });

                        navigator.clipboard.writeText(
                            JSON.stringify(eventsToCut),
                        );

                        insertHistoryAction({
                            name: "cut event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    }
                },
            },
            {
                name: "Paste",
                description: "Paste event or text",
                keyCmd: "",
                action: async function () {
                    try {
                        let findFormInput =
                            document.getElementById("FindInput");
                        let replaceFormInput =
                            document.getElementById("ReplaceInput");
                        let activeElement = document.activeElement;
                        if (activeElement === findFormInput) {
                            navigator.clipboard.readText().then((text) => {
                                findFormInput.value = text;
                            });
                        } else if (activeElement === replaceFormInput) {
                            navigator.clipboard.readText().then((text) => {
                                replaceFormInput.value = text;
                            });
                        } else if (activeElement.id === "NotesTextArea") {
                            navigator.clipboard.readText().then((text) => {
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[
                                        projectState.selected
                                    ].selected[0]
                                ].notes = text;
                            });
                        } else if (
                            activeElement.id === "ReplyTextArea" &&
                            eventGroupState[projectState.selected]?.events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ]
                        ) {
                            navigator.clipboard.readText().then((text) => {
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[
                                        projectState.selected
                                    ].selected[0]
                                ].reply = text;
                            });
                        } else if (
                            activeElement.id === "textareanotesinputlabel"
                        ) {
                            navigator.clipboard.readText().then((text) => {
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[
                                        projectState.selected
                                    ].selected[0]
                                ].notes = text;
                            });
                        } else if (
                            activeElement.id === "textareareplyinputlabel" &&
                            eventGroupState[projectState.selected]?.events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ]
                        ) {
                            navigator.clipboard.readText().then((text) => {
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[
                                        projectState.selected
                                    ].selected[0]
                                ].reply = text;
                            });
                        } else {
                            navigator.clipboard.readText().then((text) => {
                                try {
                                    let clipboardEvents = JSON.parse(text);
                                    //check if clipboardEvents is actually subtitle events
                                    if (
                                        clipboardEvents.length > 0 &&
                                        clipboardEvents[0].id &&
                                        clipboardEvents[0].style
                                    ) {
                                        let currentTime = player
                                            ? player.currentTime
                                            : 0;
                                        let newCurrentTime = currentTime;
                                        Swal.fire({
                                            ...SWAL_BASE,
                                            customClass: SWAL_CLASSES.confirmCancel,
                                            title: m.shortcut_keys_paste_events_title(),
                                            text: m.shortcut_keys_paste_events_text(),
                                            icon: "question",
                                            showCancelButton: true,
                                            confirmButtonText: m.shortcut_keys_paste_events_confirm(),
                                            cancelButtonText:
                                                m.shortcut_keys_paste_events_cancel(),
                                        })
                                            .then((sAlertRes) => {
                                                clipboardEvents.forEach(
                                                    (event, count, events) => {
                                                        // Use Sweet Alert to ask the user if they wish to paste events at the current Time. If yes, then paste the events, but update the start and end time of the event so that the start time is the current time and the end time is the current time + the duration of the event.
                                                        if (
                                                            sAlertRes.isConfirmed &&
                                                            currentTime >= 0
                                                        ) {
                                                            let duration =
                                                                event.end -
                                                                event.start;
                                                            let startTime =
                                                                count === 0
                                                                    ? currentTime
                                                                    : currentTime +
                                                                      (event.start -
                                                                          events[0]
                                                                              .end);

                                                            startTime =
                                                                startTime
                                                                    ? startTime
                                                                    : newCurrentTime;
                                                            let endTime =
                                                                startTime +
                                                                duration;
                                                            newCurrentTime =
                                                                endTime;
                                                            eventGroupState[
                                                                projectState.selected
                                                            ] = insertEvent(
                                                                eventGroupState[
                                                                    projectState
                                                                        .selected
                                                                ],
                                                                {
                                                                    ...event,
                                                                    start: startTime,
                                                                    end: endTime,
                                                                },
                                                                eventGroupState[
                                                                    projectState
                                                                        .selected
                                                                ].selected[0] +
                                                                    count +
                                                                    1,
                                                            );
                                                        } else {
                                                            eventGroupState[
                                                                projectState.selected
                                                            ] = insertEvent(
                                                                eventGroupState[
                                                                    projectState
                                                                        .selected
                                                                ],
                                                                event,
                                                                eventGroupState[
                                                                    projectState
                                                                        .selected
                                                                ].selected[0] +
                                                                    count +
                                                                    1,
                                                            );
                                                        }                                                   
                                                    },
                                                );
                                            })
                                            .catch((err) => {
                                                console.log(
                                                    "Error pasting events",
                                                );
                                                console.log(err);
                                                console.log(err.message);
                                            });
                                    } else {
                                        throw new Error(
                                            m.shortcut_keys_no_events_clipboard_error(),
                                        );
                                    }
                                } catch (err) {
                                    let range =
                                        window.quillEditor.getSelection();
                                    if (range && range.length === 0) {
                                        window.quillEditor.insertText(
                                            range.index,
                                            text,
                                        );
                                    } else if (range) {
                                        window.quillEditor.deleteText(
                                            range.index,
                                            range.length,
                                        );
                                        window.quillEditor.insertText(
                                            range.index,
                                            text,
                                        );
                                    }

                                    if (
                                        editorState.autoFormatText &&
                                        eventGroupState[projectState.selected]
                                            .selected.length > 0
                                    ) {
                                        let maxLines = eventGroupState[
                                                projectState.selected
                                            ]
                                                ? eventGroupState[
                                                      projectState.selected
                                                  ].maxLines
                                                : 2,
                                            maxChars = eventGroupState[
                                                projectState.selected
                                            ]
                                                ? eventGroupState[
                                                      projectState.selected
                                                  ].maxChars
                                                : 32,
                                            minDuration = 0,
                                            allowOrphanWords = true;

                                        try {
                                            let selectedEvent =
                                                eventGroupState[
                                                    projectState.selected
                                                ].selected[0];
                                            eventGroupState[
                                                projectState.selected
                                            ].selected = [selectedEvent];

                                            eventGroupState[
                                                projectState.selected
                                            ] = autoFormat(
                                                eventGroupState[
                                                    projectState.selected
                                                ],
                                                maxLines,
                                                maxChars,
                                                minDuration,
                                                allowOrphanWords,
                                                eventGroupState[
                                                    projectState.selected
                                                ].selected,
                                            );

                                            eventGroupState[
                                                projectState.selected
                                            ].selected = [];
                                            setTimeout(() => {
                                                eventGroupState[
                                                    projectState.selected
                                                ].selected = [selectedEvent];
                                            }, 50);
                                        } catch (err) {
                                            console.log(err.message);
                                        }
                                    }
                                }

                                insertHistoryAction({
                                    name: "paste events", //action name
                                    eventGroup: projectState.selected,
                                    snapshots: [
                                        {
                                            store: "eventGroupState",
                                            value: JSON.stringify(
                                                eventGroupState,
                                            ),
                                        },
                                    ],
                                });
                            });
                        }
                    } catch (err) {
                        console.log(err.message);
                    }
                },
            },
        ],
    },
    {
        name: "Media Player",
        shortcuts: [
            {
                name: "Insert Marker",
                description:
                    "Insert a new marker into the selected marker list at the current time",
                shortName: "insertMarker",
                keyCmd: "",
                action: async function () {
                    const selectedList =
                        markerState.lists[markerState.selected];
                    if (!selectedList) {
                        return;
                    }

                    const marker = {
                        ...new _Marker({
                            time: window.player?.currentTime || 0,
                            comment: "",
                        }),
                    };

                    selectedList.markers = [
                        ...selectedList.markers,
                        marker,
                    ];

                    selectedList.markers.sort((a, b) => {
                        return a.time - b.time;
                    });
                },
            },
            {
                name: "Insert Marker + Comment",
                description:
                    "Insert a new marker into the selected marker list at the current time and prompt for a comment",
                shortName: "insertMarkerComment",
                keyCmd: "",
                action: async function () {
                    const selectedList =
                        markerState.lists[markerState.selected];
                    if (!selectedList) {
                        return;
                    }

                    const res = await Swal.fire({
                        ...SWAL_BASE,
                        titleText: m.markers_new_marker_title(),
                        showCancelButton: true,
                        confirmButtonText: m.markers_add_marker_button(),
                        input: "textarea",
                        inputLabel: m.markers_comment_label(),
                        inputPlaceholder: m.markers_comment_placeholder(),
                        customClass: SWAL_CLASSES.confirmCancel,
                    });

                    if (!res.isConfirmed) {
                        return;
                    }

                    const marker = {
                        ...new _Marker({
                            time: window.player?.currentTime || 0,
                            comment: res.value || "",
                        }),
                    };

                    selectedList.markers = [
                        ...selectedList.markers,
                        marker,
                    ];

                    selectedList.markers.sort((a, b) => {
                        return a.time - b.time;
                    });
                },
            },
            {
                name: "Next Marker",
                description: "Go to next marker",
                shortName: "goToNextMarker",
                keyCmd: "",
                action: async function () {
                    if (
                        markerState.lists[markerState.selected] &&
                        markerState.lists[markerState.selected].markers.length >
                            0
                    ) {
                        //Go to the next closest marker based on the player.currentTime.
                        let nextMarker = markerState.lists[
                            markerState.selected
                        ].markers.find((marker) => {
                            return marker.time > player.currentTime;
                        });

                        if (nextMarker) {
                            player.currentTime = nextMarker.time;
                        }
                    }
                },
            },
            {
                name: "Previous Marker",
                description: "Go to previous marker",
                shortName: "goToPrevMarker",
                keyCmd: "",
                action: async function () {
                    if (
                        markerState.lists[markerState.selected] &&
                        markerState.lists[markerState.selected].markers.length >
                            0
                    ) {
                        //Go to the previous closest marker based on the player.currentTime.
                        let markerListReverseSort = JSON.parse(
                            JSON.stringify(
                                markerState.lists[markerState.selected].markers,
                            ),
                        ).sort((mA, mB) => {
                            return mB.time - mA.time;
                        });

                        let prevMarker = markerListReverseSort.find(
                            (marker) => {
                                return marker.time < player.currentTime;
                            },
                        );

                        if (prevMarker) {
                            player.currentTime = prevMarker.time;
                        }
                    }
                },
            },
            {
                name: "Toggle Playback (Play/Pause)",
                description: "Play or pause video playback",
                shortName: "playPause",
                keyCmd: "",

                action: async function () {
                    await togglePlay();
                },
            },
            {
                name: "Toggle Fast Forward",
                description:
                    "Fast Forward through video (foot fedal recommended)",
                shortName: "fastForward",
                keyCmd: "",

                action: async function () {
                    await toggleFastForward();
                },
            },
            {
                name: "Toggle Rewind",
                description: "Rewind through video (foot fedal recommended)",
                shortName: "rewind",
                keyCmd: "",

                action: async function () {
                    await toggleRewind();
                },
            },
            {
                name: "Reverse 1 Frame",
                description: "Go back one frame in the video",
                shortName: "reverse",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time - 1 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Forward 1 Frame",
                description: "Go forward one frame in the video",
                shortName: "forward",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time + 1 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Reverse 5 Frame",
                description: "Go back five frames in the video",
                shortName: "reverseFiveFrames",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time - 5 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Forward 5 Frame",
                description: "Go forward five frames in the video",
                shortName: "forwardFiveFrames",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time + 5 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Reverse 10 Frame",
                description: "Go back ten frames in the video",
                shortName: "reverse",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time - 10 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Forward 10 Frame",
                description: "Go forward ten frames in the video",
                shortName: "forward",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time + 10 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Reverse 12 Frame",
                description: "Go back eleven frames in the video",
                shortName: "reverseElevenFrames",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time - 12 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Forward 12 Frame",
                description: "Go forward eleven frames in the video",
                shortName: "forwardElevenFrames",
                keyCmd: "",
                action: async function () {
                    player.currentTime =
                        playerState.time + 12 / projectState.frameRate;
                    audioScrub();
                },
            },
            {
                name: "Reverse 1 Second",
                description: "Go back one second in the video",
                shortName: "reverseOneSec",
                keyCmd: "",

                action: async function () {
                    player.currentTime = playerState.time - 1;
                },
            },
            {
                name: "Forward 1 Second",
                description: "Go forward one second in the video",
                shortName: "forwardOneSec",
                keyCmd: "",

                action: async function () {
                    player.currentTime = playerState.time + 1;
                },
            },
            {
                name: "Reverse 3 Second",
                description: "Go back three second in the video",
                shortName: "reverseThreeSec",
                keyCmd: "",

                action: async function () {
                    player.currentTime = playerState.time - 3;
                },
            },
            {
                name: "Forward 3 Second",
                description: "Go forward three second in the video",
                shortName: "forwardThreeSec",
                keyCmd: "",

                action: async function () {
                    player.currentTime = playerState.time + 3;
                },
            },
            {
                name: "Go To Start Of Event",
                description:
                    "Update playhead to the start of the selected event",
                shortName: "startOfEvent",
                keyCmd: "",

                action: async function () {
                    if (
                        eventGroupState[projectState.selected].selected.length >
                            0 &&
                        eventGroupState[projectState.selected] &&
                        !isNaN(
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ].start,
                        )
                    ) {
                        player.currentTime =
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].start;
                    }
                },
            },
            {
                name: "Go To End Of Event",
                description: "Update playhead to the end of the selected event",
                shortName: "endOfEvent",
                keyCmd: "",

                action: async function () {
                    if (
                        eventGroupState[projectState.selected].selected.length >
                            0 &&
                        eventGroupState[projectState.selected] &&
                        !isNaN(
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ].end,
                        )
                    ) {
                        player.currentTime =
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].end;
                    }
                },
            },
            {
                name: "Increase Playback Speed",
                description:
                    "Increase the playback speed of the player. Playback rates include 0.25x - 16x",
                shortName: "increasePlaybackRate",
                keyCmd: "",

                action: async function () {
                    try {
                        let index = playbackSpeeds.indexOf(player.playbackRate);
                        player.playbackRate = playbackSpeeds[index + 1] || 16.0;
                        updatePlayerSpeed(player.playbackRate);
                    } catch (err) {
                        console.warn(err, err.message);
                    }
                },
            },
            {
                name: "Decrease Playback Speed",
                description:
                    "Decrease the playback speed of the player. Playback rates include 0.25x - 16x",
                shortName: "decreasePlaybackRate",
                keyCmd: "",

                action: async function () {
                    try {
                        let index = playbackSpeeds.indexOf(player.playbackRate);
                        player.playbackRate = playbackSpeeds[index - 1] || 0.25;
                        updatePlayerSpeed(player.playbackRate);
                    } catch (err) {
                        console.warn(err, err.message);
                    }
                },
            },
            {
                name: "Toggle Caption Lock",
                description: "Toggle caption lock on/off",
                shortName: "toggleCaptionLock",
                keyCmd: "",

                action: async function () {
                    try {
                        lockState.caption = !lockState.caption;
                    } catch (err) {
                        console.warn(err, err.message);
                    }
                },
            },
            {
                name: "Toggle Video Lock",
                description: "Toggle video lock on/off",
                shortName: "toggleVideoLock",
                keyCmd: "",

                action: async function () {
                    try {
                        lockState.video = !lockState.video;
                    } catch (err) {
                        console.warn(err, err.message);
                    }
                },
            },
            {
                name: "Toggle Play Preview",
                description: "Toggle play preview on/off",
                shortName: "togglePreviewLock",
                keyCmd: "",

                action: async function () {
                    try {
                        lockState.preview = !lockState.preview;
                    } catch (err) {
                        console.warn(err, err.message);
                    }
                },
            },
        ],
    },
    {
        name: "Editor",
        shortcuts: [
            {
                name: "Select Next Event",
                description: "Select the next event in the list",
                shortName: "selectNextEditorEvent",
                keyCmd: "",
                action: async function () {
                    const eventGroup = eventGroupState[projectState.selected];

                    if (!eventGroup) {
                        return;
                    }

                    selectEditorEvent((eventGroup.selected?.[0] ?? -1) + 1);
                },
            },
            {
                name: "Select Previous Event",
                description: "Select the previous event in the list",
                shortName: "selectPrevEditorEvent",
                keyCmd: "",
                action: async function () {
                    const eventGroup = eventGroupState[projectState.selected];

                    if (!eventGroup) {
                        return;
                    }

                    selectEditorEvent((eventGroup.selected?.[0] ?? 0) - 1);
                },
            },
            {
                name: "Go To First Event",
                description: "Select the first event in the list",
                shortName: "goToFirstEditorEvent",
                keyCmd: "",
                action: async function () {
                    selectEditorEvent(0);
                },
            },
            {
                name: "Go To Last Event",
                description: "Select the last event in the list",
                shortName: "goToLastEditorEvent",
                keyCmd: "",
                action: async function () {
                    const eventGroup = eventGroupState[projectState.selected];

                    if (!eventGroup || !eventGroup.events) {
                        return;
                    }

                    selectEditorEvent(eventGroup.events.length - 1);
                },
            },
            {
                name: "Delete Event",
                description: "Delete selected event(s)",
                shortName: "deleteEvent",
                keyCmd: "Delete",
                action: async function () {
                    if (!eventGroupState[projectState.selected]) {
                        return;
                    }

                    let swalRes = await Swal.fire({
                        ...SWAL_BASE,
                        customClass: SWAL_CLASSES.destructive,
                        title: m.shortcut_keys_delete_event_title(),
                        text: m.shortcut_keys_delete_event_text(),
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: m.shortcut_keys_delete_event_confirm(),
                        cancelButtonText: m.shortcut_keys_delete_event_cancel(),
                    });

                    if (!swalRes.isConfirmed) {
                        return;
                    }

                    eventGroupState[projectState.selected].selected.sort(
                        (a, b) => {
                            return b - a;
                        },
                    );

                    let lastEvent =
                        eventGroupState[projectState.selected].selected[
                            eventGroupState[projectState.selected].selected
                                .length - 1
                        ] - 1;

                    eventGroupState[projectState.selected].selected.forEach(
                        (eventIndex) => {
                            eventGroupState[projectState.selected] =
                                removeEvent(
                                    eventGroupState[projectState.selected],
                                    eventIndex,
                                );
                        },
                    );

                    eventGroupState[projectState.selected].selected =
                        lastEvent >= 0 ? [lastEvent] : [];

                    insertHistoryAction({
                        name: "remove event(s)", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Insert Event (Above)",
                description: "Insert event above the currently selected event.",
                shortName: "insertEventBefore",
                keyCmd: "",
                action: async function () {
                    if (!eventGroupState[projectState.selected]) {
                        return;
                    }

                    let eventOptions;
                    let eventIndex =
                        eventGroupState[projectState.selected].selected[0] ?? 0;
                    let prevEventIndex = eventIndex - 1;
                    let nxtEventIndex = eventIndex + 1;

                    if (
                        eventGroupState[projectState.selected].events.length > 0
                    ) {
                        try {
                            let eventTemplate =
                                eventGroupState[projectState.selected].selected
                                    .length > 0
                                    ? eventGroupState[projectState.selected]
                                          .events[
                                          eventGroupState[projectState.selected]
                                              .selected[0]
                                      ]
                                    : eventGroupState[projectState.selected]
                                          .events[
                                          eventGroupState[projectState.selected]
                                              .events.length - 1
                                      ];

                            eventOptions = {
                                alignment: eventTemplate.alignment,
                                xPos: eventTemplate.xPos,
                                yPos: eventTemplate.yPos,
                                vertical: eventTemplate.vertical,
                                voice: eventTemplate.voice,
                                speakingStyle: eventTemplate.speakingStyle,
                                style: eventTemplate.style,
                                rate: eventTemplate.rate,
                                track: eventTemplate.track ?? false,
                                start: editorState.insertStartOnInsert
                                    ? player.currentTime
                                    : false,
                                end: editorState.insertStartOnInsert
                                    ? player.currentTime +
                                      eventGroupState[projectState.selected]
                                          .minDuration
                                    : false,
                            };
                        } catch (err) {
                            console.log(err);
                        }
                    } else {
                        eventOptions = {
                            alignment:
                                ["subtitle", "translation"].indexOf(
                                    eventGroupState[projectState.selected].type,
                                ) > -1
                                    ? "center"
                                    : "left",
                            start: editorState.insertStartOnInsert
                                ? player.currentTime
                                : false,
                            end: editorState.insertStartOnInsert
                                ? player.currentTime +
                                  eventGroupState[projectState.selected]
                                      .minDuration
                                : false,
                            voice: eventGroupState[projectState.selected].ad
                                .voice,
                        };
                    }

                    /* Insert End On Insert Option */
                    if (
                        prevEventIndex > -1 &&
                        editorState.insertEndOnInsert &&
                        eventGroupState[projectState.selected].selected.length >
                            0
                    ) {
                        eventGroupState[projectState.selected].events[
                            prevEventIndex
                        ].end = eventOptions.start;
                    }

                    eventGroupState[projectState.selected] = insertEvent(
                        eventGroupState[projectState.selected],
                        eventOptions,
                        eventIndex,
                    );

                    eventGroupState[projectState.selected].selected = [
                        nxtEventIndex,
                    ];

                    insertHistoryAction({
                        name: "insert event", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Insert Event (Below)",
                description: "Insert new event below the selected event",
                shortName: "insertEvent",
                keyCmd: "",
                action: async function () {
                    if (!eventGroupState[projectState.selected]) {
                        return;
                    }

                    let eventOptions;
                    let eventIndex =
                        eventGroupState[projectState.selected].selected.length >
                        0
                            ? eventGroupState[projectState.selected]
                                  .selected[0] + 1
                            : null;

                    try {
                        let eventTemplate =
                            eventGroupState[projectState.selected].selected
                                .length > 0
                                ? eventGroupState[projectState.selected].events[
                                      eventGroupState[projectState.selected]
                                          .selected[0]
                                  ]
                                : eventGroupState[projectState.selected].events[
                                      eventGroupState[projectState.selected]
                                          .events.length - 1
                                  ];

                        if (!eventTemplate) {
                            eventOptions = {
                                alignment:
                                    ["subtitle", "translation"].indexOf(
                                        eventGroupState[projectState.selected]
                                            .type,
                                    ) > -1
                                        ? "center"
                                        : "left",
                                start: editorState.insertStartOnInsert
                                    ? player.currentTime
                                    : false,
                                end: editorState.insertStartOnInsert
                                    ? player.currentTime +
                                      eventGroupState[projectState.selected]
                                          .minDuration
                                    : false,
                                voice: eventGroupState[projectState.selected].ad
                                    .voice,
                            };
                        } else {
                            eventOptions = {
                                alignment: eventTemplate.alignment,
                                xPos: eventTemplate.xPos,
                                yPos: eventTemplate.yPos,
                                writingMode: eventTemplate.writingMode,
                                voice: eventTemplate.voice,
                                speakingStyle: eventTemplate.speakingStyle,
                                style: eventTemplate.style,
                                rate: eventTemplate.rate,
                                track: eventTemplate.track ?? false,
                                start: editorState.insertStartOnInsert
                                    ? player.currentTime
                                    : false,
                                end: editorState.insertStartOnInsert
                                    ? player.currentTime +
                                      eventGroupState[projectState.selected]
                                          .minDuration
                                    : false,
                            };
                        }
                    } catch (err) {
                        console.log(err);
                    }
                    /* Insert End On Insert Option */
                    if (
                        editorState.insertEndOnInsert &&
                        eventGroupState[projectState.selected].selected.length >
                            0
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventGroupState[projectState.selected].selected[0]
                        ].end =
                            eventOptions.start -
                            editorState.minFrameGap / projectState.frameRate;
                    }

                    eventGroupState[projectState.selected] = insertEvent(
                        eventGroupState[projectState.selected],
                        eventOptions,
                        eventIndex,
                    );

                    eventGroupState[projectState.selected].selected =
                        eventIndex !== null
                            ? [eventIndex]
                            : [
                                  eventGroupState[projectState.selected].events
                                      .length - 1,
                              ];

                    window.dispatchEvent(
                        new CustomEvent("scrollToVirtualListItem", {
                            detail: { index: eventIndex },
                        }),
                    );

                    insertHistoryAction({
                        name: "insert event", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Go To Event",
                description:
                    "Open the Go To Event window to navigate to a specific event in the selected Event Group",
                shortName: "goToEvent",
                keyCmd: "",

                action: async function () {
                    if (
                        eventGroupState.length > 0 &&
                        eventGroupState[projectState.selected]
                    ) {
                        showModal("goToEvent");
                    }
                },
            },
            {
                name: "Insert Speaker",
                description: "Open the Insert Speaker Name window",
                shortName: "insertSpeaker",
                keyCmd: "",
                action: async function () {
                    showModal("insertSpeaker");
                },
            },
            {
                name: "Toggle Edit Mode",
                description:
                    "Toggling Edit Mode will focus the textarea of the selected Event. This will allow you to quickly edit the text of the Event. Disabling Edit Mode will return the focus to the Event List.",
                shortName: "toggleEditMode",
                keyCmd: "",
                action: async function () {
                    try {
                        editorState.editing = !editorState.editing;
                        if (editorState.editing && window.quillEditor) {
                            //Check if window.quillEditor is initialized and focus on it
                            window.quillEditor.setSelection(
                                window.quillEditor.getLength(),
                                0,
                            );
                        } else if (window.quillEditor) {
                            window.quillEditor.blur();               
                            document.body.focus();        
                        }
                    } catch (err) {
                        console.log(
                            "Toggle Edit Mode Shortcut Error: ",
                            err.message,
                        );
                    }
                },
            },
            {
                name: "Set Incode (Start)",
                description:
                    "Update the incode of the selected Event based on the current media time",
                shortName: "markIn",
                keyCmd: "",

                action: async function () {
                    if (
                        eventGroupState[projectState.selected]?.selected
                            .length === 0 ||
                        !window.player
                    ) {
                        return;
                    }

                    let eventIndex =
                        eventGroupState[projectState.selected].selected[0];
                    let originalDuration =
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end === false ||
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start === false
                            ? 2
                            : eventGroupState[projectState.selected].events[
                                  eventIndex
                              ].end -
                              eventGroupState[projectState.selected].events[
                                  eventIndex
                              ].start;
                    let originalId =
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].id;

                    eventGroupState[projectState.selected].events[
                        eventIndex
                    ].start = window.player.currentTime;

                    if (
                        isNaN(
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].end,
                        )
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end = player.currentTime + 2;
                    } else if (
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end <
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end =
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].start + originalDuration;
                    }

                    //If the minimum frame gap is set and there is an overlap with the previoud event in the eventgroup then adjust the end time of the previous event
                    if (
                        editorState.minFrameGap > 0 &&
                        eventIndex > 0 &&
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start <=
                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].end
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex - 1
                        ].end =
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].start -
                            editorState.minFrameGap / projectState.frameRate;
                    }

                    //Select the event that was just updated
                    let newIndex = eventGroupState[
                        projectState.selected
                    ].events.findIndex((event) => event.id === originalId);
                    if (
                        editorState.selectNextEvent &&
                        eventGroupState[projectState.selected].events[
                            newIndex + 1
                        ]
                    ) {
                        eventGroupState[projectState.selected].selected = [
                            newIndex + 1,
                        ];
                    } else {
                        eventGroupState[projectState.selected].selected = [
                            newIndex,
                        ];
                    }

                    /* Auto Chain Events */
                    if (
                        editorState.autoChain &&
                        eventGroupState[projectState.selected].events[
                            newIndex - 1
                        ]
                    ) {
                        eventGroupState[projectState.selected].events[
                            newIndex - 1
                        ].end =
                            eventGroupState[projectState.selected].events[
                                newIndex
                            ].start -
                            editorState.minFrameGap / projectState.frameRate;
                    }

                    insertHistoryAction({
                        name: "update timecode", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Set Outcode (End)",
                description:
                    "Update the outcode of the selected Event based on the current media time",
                shortName: "markOut",
                keyCmd: "",

                action: async function () {
                    if (
                        eventGroupState[projectState.selected]?.selected
                            .length === 0 ||
                        !window.player
                    ) {
                        return;
                    }

                    let eventIndex =
                        eventGroupState[projectState.selected].selected[0];
                    let originalDuration =
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end === false ||
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start === false
                            ? 2
                            : eventGroupState[projectState.selected].events[
                                  eventIndex
                              ].end -
                              eventGroupState[projectState.selected].events[
                                  eventIndex
                              ].start;
                    let originalId =
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].id;

                    eventGroupState[projectState.selected].events[
                        eventIndex
                    ].end = window.player.currentTime;

                    if (
                        isNaN(
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].start,
                        )
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start = player.currentTime - 2;
                    } else if (
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start >
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].start =
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].end - originalDuration;
                    }

                    //If the minimum frame gap is set and there is an overlap with the next event in the eventgroup then adjust the start time of the next event
                    if (
                        editorState.minFrameGap > 0 &&
                        eventIndex <
                            eventGroupState[projectState.selected].events
                                .length -
                                1 &&
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].end >=
                            eventGroupState[projectState.selected].events[
                                eventIndex + 1
                            ].start
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex + 1
                        ].start =
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].end +
                            editorState.minFrameGap / projectState.frameRate;
                    }

                    //Select the event that was just updated
                    let newIndex = eventGroupState[
                        projectState.selected
                    ].events.findIndex((event) => event.id === originalId);

                    eventGroupState[projectState.selected].selected = [
                        newIndex
                    ];

                    insertHistoryAction({
                        name: "update timecode", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Add 1 Frame To Start",
                description: "Increase start time by one frame",
                shortName: "addFrameToStart",
                keyCmd: "",

                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].start += 1 / projectState.frameRate;

                            if (lockState.video) {
                                player.currentTime =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].start;
                            }

                            insertHistoryAction({
                                name: "add frame", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Add 1 Frame To End",
                description: "Increase end time by one frame",
                shortName: "addFrameToEnd",
                keyCmd: "",

                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].end += 1 / projectState.frameRate;

                            if (
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] + 1
                                ] &&
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] + 1
                                ].start <
                                    eventGroupState[projectState.selected]
                                        .events[
                                        eventGroupState[projectState.selected]
                                            .selected[0]
                                    ].end
                            ) {
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] + 1
                                ].start =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].end;
                            }

                            if (lockState.video) {
                                player.currentTime =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].end;
                            }

                            insertHistoryAction({
                                name: "add frame", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Subtract 1 Frame From Start",
                description: "Decrease start time by one frame",
                shortName: "removeFrameFromStart",
                keyCmd: "",

                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].start = Math.max(
                                0,
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ].start -
                                    1 / projectState.frameRate,
                            );

                            if (
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] - 1
                                ] &&
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] - 1
                                ].end >
                                    eventGroupState[projectState.selected]
                                        .events[
                                        eventGroupState[projectState.selected]
                                            .selected[0]
                                    ].start
                            ) {
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] - 1
                                ].end =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].start;
                            }

                            if (lockState.video) {
                                player.currentTime =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].start;
                            }

                            insertHistoryAction({
                                name: "subtract frame", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Subtract 1 Frame From End",
                description: "Decrease end time by one frame",
                shortName: "removeFrameFromEnd",
                keyCmd: "",

                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            eventGroupState[projectState.selected].events[
                                eventGroupState[
                                    projectState.selected
                                ].selected[0]
                            ].end = Math.max(
                                0,
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ].end -
                                    1 / projectState.frameRate,
                            );

                            if (lockState.video) {
                                player.currentTime =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].end;
                            }

                            insertHistoryAction({
                                name: "subtract frame", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Shift Event Backward",
                description:
                    "Shift the selected event(s) backward by one frame",
                shortName: "shiftEventBackward",
                keyCmd: "",
                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            let firstSelectedEventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];

                            eventGroupState[
                                projectState.selected
                            ].selected.forEach((eventIndex) => {
                                //Check that the start time of the event is greater than 0
                                if (
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].start >= 0
                                ) {
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].start -=
                                        1 / projectState.frameRate;
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].end -=
                                        1 / projectState.frameRate;
                                }
                            });

                            if (lockState.video) {
                                player.currentTime =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[firstSelectedEventIndex].start;
                            }

                            insertHistoryAction({
                                name: "shift timecodes", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Shift Event Forward",
                description: "Shift the selected event(s) forward by one frame",
                shortName: "shiftEventForward",
                keyCmd: "",
                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            let firstSelectedEventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];

                            eventGroupState[
                                projectState.selected
                            ].selected.forEach((eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].start += 1 / projectState.frameRate;
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].end += 1 / projectState.frameRate;
                            });

                            if (lockState.video) {
                                player.currentTime =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[firstSelectedEventIndex].start;
                            }

                            insertHistoryAction({
                                name: "shift timecodes", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Offset Timecode",
                description:
                    "Offset the timecode of the selected event(s) based on the playhead position",
                shortName: "tcOffset",
                keyCmd: "",

                action: async function () {
                    try {
                        if (
                            !eventGroupState[projectState.selected] ||
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        ) {
                            return;
                        }

                        let offsetSec =
                            player.currentTime -
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ].start;

                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                if (
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex]
                                ) {
                                    //Update Start
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].start += offsetSec;

                                    //Update End
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].end += offsetSec;
                                }
                            },
                        );

                        /* SAVE SNAPSHOT TO HISTORY STORE */
                        insertHistoryAction({
                            name: "apply offset", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(err.message, {
                            classes: ["toast-danger"],
                        });
                    }
                },
            },
            {
                name: "Toggle Forced Narrative Subtitles",
                description:
                    "Mark selected event(s) as Forced Narrative Subtitles",
                shortName: "markForcedSubtitle",
                keyCmd: "",
                action: async function () {
                    try {
                        if (
                            !eventGroupState[projectState.selected] ||
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        ) {
                            return;
                        }

                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].forced =
                                    !eventGroupState[projectState.selected]
                                        .events[eventIndex].forced;
                            },
                        );

                        /* Store in History */
                        insertHistoryAction({
                            name: "mark as FNS", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Assign Track None",
                description: "Clear the track assignment of the selected event(s)",
                shortName: "assignTrackNone",
                keyCmd: "Ctrl+Alt+Digit0",
                action: async function () {
                    applySelectedEventTrack(false);
                },
            },
            {
                name: "Assign Track A",
                description: "Assign the selected event(s) to track A",
                shortName: "assignTrackA",
                keyCmd: "Ctrl+Alt+Digit1",
                action: async function () {
                    applySelectedEventTrack("A");
                },
            },
            {
                name: "Assign Track B",
                description: "Assign the selected event(s) to track B",
                shortName: "assignTrackB",
                keyCmd: "Ctrl+Alt+Digit2",
                action: async function () {
                    applySelectedEventTrack("B");
                },
            },
            {
                name: "Assign Track C",
                description: "Assign the selected event(s) to track C",
                shortName: "assignTrackC",
                keyCmd: "Ctrl+Alt+Digit3",
                action: async function () {
                    applySelectedEventTrack("C");
                },
            },
            {
                name: "Assign Track D",
                description: "Assign the selected event(s) to track D",
                shortName: "assignTrackD",
                keyCmd: "Ctrl+Alt+Digit4",
                action: async function () {
                    applySelectedEventTrack("D");
                },
            },
            {
                name: "Insert Ellipsis",
                description: "Insert ellipsis",
                shortName: "insertEllipsis",
                keyCmd: "",
                action: async function () {
                    try {
                        if (!window.quillEditor) {
                            return;
                        }

                        let range = window?.quillEditor?.getSelection();
                        if (range && range.length === 0) {
                            window.quillEditor.insertText(range.index, "…");
                        } else if (range) {
                            window.quillEditor.deleteText(
                                range.index,
                                range.length,
                            );
                            window.quillEditor.insertText(range.index, "…");
                        }

                        insertHistoryAction({
                            name: "update text", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Insert ♪",
                description: "Insert single music note",
                shortName: "insertMusicNote",
                keyCmd: "",

                action: async function () {
                    try {
                        if (!window.quillEditor) {
                            return;
                        }
                        let range = window?.quillEditor?.getSelection();
                        if (range && range.length === 0) {
                            window.quillEditor.insertText(range.index, "♪");
                        } else if (range) {
                            window.quillEditor.deleteText(
                                range.index,
                                range.length,
                            );
                            window.quillEditor.insertText(range.index, "♪");
                        }

                        insertHistoryAction({
                            name: "update text", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Insert Surround ♪",
                description:
                    "Insert surround single music notes to all selected events",
                shortName: "insertSurroundMusicNotes",
                keyCmd: "",
                action: async function () {
                    try {
                        let insertChar = "♪",
                            alignmentMap = {
                                left: false,
                                right: "right",
                                center: "center",
                            };

                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex, count) => {
                                let startIndex =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.indexOf(">") + 1;
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        0,
                                        startIndex,
                                    ) +
                                    insertChar +
                                    " " +
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        startIndex,
                                    );

                                let endIndex =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.lastIndexOf("<");

                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        0,
                                        endIndex,
                                    ) +
                                    " " +
                                    insertChar +
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        endIndex,
                                    );

                                if (count === 0 && window.quillEditor) {
                                    let delta =
                                        window.quillEditor.clipboard.convert({
                                            html: eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].text,
                                        });
                                    window.quillEditor.setContents(
                                        delta,
                                        "silent",
                                    );
                                    window.quillEditor.formatLine(
                                        0,
                                        99999,
                                        "align",
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].alignment ===
                                            "left"
                                            ? ""
                                            : event.alignment,
                                        "silent",
                                    );
                                }
                            },
                        );

                        insertHistoryAction({
                            name: "update text", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Insert Surround ♪♪",
                description: "Insert surround ♪♪ to all selected events",
                shortName: "insertDblSurroundMusicNotes",
                keyCmd: "",

                action: async function () {
                    try {
                        let insertChar = "♪♪",
                            alignmentMap = {
                                left: false,
                                right: "right",
                                center: "center",
                            };

                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex, count) => {
                                let startIndex =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.indexOf(">") + 1;
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        0,
                                        startIndex,
                                    ) +
                                    insertChar +
                                    " " +
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        startIndex,
                                    );

                                let endIndex =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.lastIndexOf("<");

                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        0,
                                        endIndex,
                                    ) +
                                    " " +
                                    insertChar +
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].text.substring(
                                        endIndex,
                                    );

                                if (count === 0 && window.quillEditor) {
                                    let delta =
                                        window.quillEditor.clipboard.convert({
                                            html: eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].text,
                                        });
                                    window.quillEditor.setContents(
                                        delta,
                                        "silent",
                                    );
                                    window.quillEditor.formatLine(
                                        0,
                                        9999,
                                        "align",
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].alignment ===
                                            "left"
                                            ? ""
                                            : eventGroupState[
                                                  projectState.selected
                                              ].events[eventIndex].alignment,
                                        "silent",
                                    );
                                }
                            },
                        );

                        insertHistoryAction({
                            name: "update text", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Highlight Next Event",
                description: "Add next Event to Selected Events",
                shortName: "highlightNextEvent",
                keyCmd: "",
                action: async function () {
                    try {
                        if (!eventGroupState[projectState.selected]) {
                            return;
                        }

                        if (
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        ) {
                            return;
                        }

                        let nextEventIndex =
                            eventGroupState[projectState.selected].selected[0] +
                            1;
                        if (
                            eventGroupState[projectState.selected].events[
                                nextEventIndex
                            ]
                        ) {
                            let selectedEvents = [
                                nextEventIndex,
                                ...eventGroupState[projectState.selected]
                                    .selected,
                            ];
                            eventGroupState[projectState.selected].selected =
                                selectedEvents.filter(
                                    (item, index) =>
                                        selectedEvents.indexOf(item) === index,
                                );

                            window.dispatchEvent(
                                new CustomEvent("scrollToVirtualListItem", {
                                    detail: { index: nextEventIndex },
                                }),
                            );
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Highlight Previous Event",
                description: "Add previous Event to Selected Events",
                shortName: "highlightPrevEvent",
                keyCmd: "",
                action: async function () {
                    try {
                        if (!eventGroupState[projectState.selected]) {
                            return;
                        }

                        if (
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        ) {
                            return;
                        }

                        let prevEventIndex =
                            eventGroupState[projectState.selected].selected[0] -
                            1;
                        if (
                            eventGroupState[projectState.selected].events[
                                prevEventIndex
                            ]
                        ) {
                            let selectedEvents = [
                                prevEventIndex,
                                ...eventGroupState[projectState.selected]
                                    .selected,
                            ];
                            eventGroupState[projectState.selected].selected =
                                selectedEvents.filter(
                                    (item, index) =>
                                        selectedEvents.indexOf(item) === index,
                                );

                            window.dispatchEvent(
                                new CustomEvent("scrollToVirtualListItem", {
                                    detail: { index: prevEventIndex },
                                }),
                            );
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Select All Events",
                description: "Select all events in event group",
                shortName: "selectAllEvents",
                keyCmd: "",
                action: async function () {
                    try {
                        if (
                            window.quillEditor &&
                            window.quillEditor.hasFocus()
                        ) {
                            window.quillEditor.setSelection(
                                0,
                                window.quillEditor.getLength(),
                            );
                        } else if (isInputFocused()) {
                            //Select all text in the focused input
                            document.activeElement.select();
                        } else {
                            const events = eventGroupState[projectState.selected].events;
                            const eventGroup = eventGroupState[projectState.selected];
                            eventGroupState[projectState.selected].selected = getFilteredEventIndices(events, eventGroup, filterState);
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Select All Events From Current",
                description:
                    "Select all Events in Event Group starting the currently selected event.",
                shortName: "selectAllEventsFromCurrent",
                keyCmd: "",
                action: async function () {
                    try {
                        if (
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            const currentIndex = eventGroupState[projectState.selected].selected[0];
                            const events = eventGroupState[projectState.selected].events;
                            const eventGroup = eventGroupState[projectState.selected];
                            const filteredIndices = getFilteredEventIndices(events, eventGroup, filterState);
                            // Filter to only include indices >= current selection
                            eventGroupState[projectState.selected].selected = filteredIndices.filter(index => index >= currentIndex);
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Move Word Up",
                description: "Move a single word up to the previous line",
                shortName: "moveWordUp",
                keyCmd: "",
                action: async function () {
                    try {
                        if (window.quillEditor) {
                            //Check that the selected event has text
                            let plainText = window.quillEditor.getText();
                            let format = window.quillEditor.getFormat();
                            let range = window?.quillEditor?.getSelection();

                            if (
                                !plainText ||
                                !range ||
                                plainText.trim().split(" ").length === 1
                            ) {
                                return;
                            }

                            //Get the current line of the cursor
                            let cursorLine =
                                plainText.substring(0, range.index).split("\n")
                                    .length - 1;

                            let textLines = plainText
                                .split("\n")
                                .filter((line) => line.trim() !== "");
                            let words = textLines[cursorLine].split(" ");

                            if (cursorLine > 0) {
                                textLines[cursorLine - 1] +=
                                    " " + words.shift();
                                textLines[cursorLine] = words.join(" ");
                            } else if (words.length > 0) {
                                textLines.unshift(words.shift());
                                textLines[cursorLine + 1] = words.join(" ");
                            }

                            //Remove empty lines
                            textLines = textLines.filter(
                                (line) => line.trim() !== "",
                            );
                            window.quillEditor.setText(textLines.join("\n"));
                            Object.keys(format).forEach((key) => {
                                window.quillEditor.formatText(
                                    0,
                                    window.quillEditor.getLength(),
                                    key,
                                    format[key],
                                );
                            });

                            window.quillEditor.setSelection(range.index, 0);

                            insertHistoryAction({
                                name: "move word", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });

                            return;
                        }
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(err.message, {
                            classes: ["toast-danger"],
                        });
                    }
                },
            },
            {
                name: "Move Word Down",
                description: "Move a single word down to the next line",
                shortName: "moveWordDown",
                keyCmd: "",
                action: async function () {
                    try {
                        if (window.quillEditor) {
                            //Check that the selected event has text
                            let plainText = window.quillEditor.getText();
                            let format = window.quillEditor.getFormat();
                            let range = window?.quillEditor?.getSelection();

                            if (
                                !plainText ||
                                !range ||
                                plainText.trim().split(" ").length === 1
                            ) {
                                return;
                            }

                            //Get the current line of the cursor
                            let cursorLine =
                                plainText.substring(0, range.index).split("\n")
                                    .length - 1;

                            let textLines = plainText
                                .split("\n")
                                .filter((line) => line.trim() !== "");
                            let words = textLines[cursorLine].split(" ");

                            if (cursorLine < textLines.length - 1) {
                                textLines[cursorLine + 1] =
                                    words.pop() +
                                    " " +
                                    textLines[cursorLine + 1];
                                textLines[cursorLine] = words.join(" ");
                            } else if (words.length > 0) {
                                textLines.push(words.pop());
                                textLines[cursorLine] = words.join(" ");
                            }

                            //Remove empty lines
                            textLines = textLines.filter(
                                (line) => line.trim() !== "",
                            );
                            window.quillEditor.setText(textLines.join("\n"));
                            Object.keys(format).forEach((key) => {
                                window.quillEditor.formatText(
                                    0,
                                    window.quillEditor.getLength(),
                                    key,
                                    format[key],
                                );
                            });

                            window.quillEditor.setSelection(range.index, 0);
                            insertHistoryAction({
                                name: "move word", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });

                            return;
                        }
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(err.message, {
                            classes: ["toast-danger"],
                        });
                    }
                },
            },
            {
                name: "Move Word To Previous Event",
                description:
                    "Shift a single word back to the end of the previous Event",
                shortName: "shiftWordBack",
                keyCmd: "",
                action: async function () {
                    try {
                        if (!eventGroupState[projectState.selected]) return;
                        if (
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        )
                            return;
                        if (
                            eventGroupState[projectState.selected]
                                .selected[0] === 0
                        )
                            return;

                        eventGroupState[projectState.selected].selected = [
                            eventGroupState[projectState.selected].selected[0],
                        ];

                        let res = shiftWordBack(
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0] - 1
                            ],
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ],
                            eventGroupState[projectState.selected].maxChars,
                        );

                        // Replace with plain objects so un-proxied Event class instances re-render.
                        let events = eventGroupState[projectState.selected].events;
                        let currentIndex = eventGroupState[projectState.selected].selected[0];
                        events[currentIndex - 1] = { ...events[currentIndex - 1], text: res.event1.text };
                        events[currentIndex] = { ...events[currentIndex], text: res.event2.text };

                        if (window.quillEditor) {
                            let eventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];
                            let delta = window.quillEditor.clipboard.convert({
                                html: eventGroupState[projectState.selected]
                                    .events[eventIndex].text,
                            });
                            window.quillEditor.setContents(delta, "silent");
                            window.quillEditor.formatLine(
                                0,
                                9999,
                                "align",
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].alignment === "left"
                                    ? ""
                                    : eventGroupState[projectState.selected]
                                          .events[eventIndex].alignment,
                                "silent",
                            );
                        }

                        if (editorState.updateTimeOnEdit) {
                            let currentEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ];
                            let lastEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] - 1
                                ];

                            if (
                                currentEvent.start &&
                                currentEvent.end &&
                                lastEvent.start &&
                                lastEvent.end
                            ) {
                                let avgTimePerWord =
                                    getAvgTimePerWord(currentEvent);
                                if (avgTimePerWord > 0 && avgTimePerWord !== Infinity && !isNaN(avgTimePerWord)) {
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[projectState.selected]
                                            .selected[0] - 1
                                    ].end += avgTimePerWord;
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].start += avgTimePerWord;
                                }
                            }
                        }

                        insertHistoryAction({
                            name: "shift word", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Move Word To Next Event",
                description:
                    "Shift a single word forward to the beginning of the next Event",
                shortName: "shiftWordForward",
                keyCmd: "",

                action: async function () {
                    try {
                        if (!eventGroupState[projectState.selected]) return;
                        if (
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        )
                            return;
                        if (
                            eventGroupState[projectState.selected]
                                .selected[0] ===
                            eventGroupState[projectState.selected].events
                                .length -
                                1
                        )
                            return;

                        eventGroupState[projectState.selected].selected = [
                            eventGroupState[projectState.selected].selected[0],
                        ];

                        let res = shiftWordForward(
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ],
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0] + 1
                            ],
                            eventGroupState[projectState.selected].maxChars,
                        );

                        // Replace with plain objects so un-proxied Event class instances re-render.
                        let events = eventGroupState[projectState.selected].events;
                        let currentIndex = eventGroupState[projectState.selected].selected[0];
                        events[currentIndex] = { ...events[currentIndex], text: res.event1.text };
                        events[currentIndex + 1] = { ...events[currentIndex + 1], text: res.event2.text };

                        if (window.quillEditor) {
                            let eventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];
                            let delta = window.quillEditor.clipboard.convert({
                                html: eventGroupState[projectState.selected]
                                    .events[eventIndex].text,
                            });
                            window.quillEditor.setContents(delta, "silent");
                            window.quillEditor.formatLine(
                                0,
                                9999,
                                "align",
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].alignment === "left"
                                    ? ""
                                    : eventGroupState[projectState.selected]
                                          .events[eventIndex].alignment,
                                "silent",
                            );
                        }

                        if (editorState.updateTimeOnEdit) {
                            let currentEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ];
                            let nextEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] + 1
                                ];

                            if (
                                currentEvent.start &&
                                currentEvent.end &&
                                nextEvent.start &&
                                nextEvent.end
                            ) {
                                let avgTimePerWord =
                                    getAvgTimePerWord(currentEvent);

                                if (avgTimePerWord > 0 && avgTimePerWord !== Infinity && !isNaN(avgTimePerWord)) {
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[projectState.selected]
                                            .selected[0] + 1
                                    ].start -= avgTimePerWord;
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].end -= avgTimePerWord;
                                }
                            }
                        }

                        insertHistoryAction({
                            name: "shift word", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Move Line Up",
                description:
                    "Shift line backwards to the end of the previous Event",
                shortName: "shiftLineBack",
                keyCmd: "",

                action: async function () {
                    try {
                        if (!eventGroupState[projectState.selected]) return;
                        if (
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        )
                            return;
                        if (
                            eventGroupState[projectState.selected]
                                .selected[0] === 0
                        )
                            return;

                        eventGroupState[projectState.selected].selected = [
                            eventGroupState[projectState.selected].selected[0],
                        ];

                        let res = shiftLineBack(
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0] - 1
                            ],
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ],
                        );

                        // Replace with plain objects so un-proxied Event class instances re-render.
                        let events = eventGroupState[projectState.selected].events;
                        let currentIndex = eventGroupState[projectState.selected].selected[0];
                        events[currentIndex - 1] = { ...events[currentIndex - 1], text: res.event1.text };
                        events[currentIndex] = { ...events[currentIndex], text: res.event2.text };

                        if (window.quillEditor) {
                            let eventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];
                            let delta = window.quillEditor.clipboard.convert({
                                html: eventGroupState[projectState.selected]
                                    .events[eventIndex].text,
                            });
                            window.quillEditor.setContents(delta, "silent");
                            window.quillEditor.formatLine(
                                0,
                                9999,
                                "align",
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].alignment === "left"
                                    ? ""
                                    : eventGroupState[projectState.selected]
                                          .events[eventIndex].alignment,
                                "silent",
                            );
                        }

                        if (editorState.updateTimeOnEdit) {
                            let currentEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ];
                            let lastEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] - 1
                                ];

                            if (
                                currentEvent.start &&
                                currentEvent.end &&
                                lastEvent.start &&
                                lastEvent.end
                            ) {
                                let avgTimePerWord =
                                    getAvgTimePerWord(currentEvent);
                                let numberOfWordsInLastLineOfLastEvent =
                                    window.quillEditor
                                        .getText(0)
                                        .split("\n")
                                        .pop()
                                        .split(" ").length;
                                if (avgTimePerWord > 0 && avgTimePerWord !== Infinity) {
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[projectState.selected]
                                            .selected[0] - 1
                                    ].end +=
                                        avgTimePerWord *
                                        numberOfWordsInLastLineOfLastEvent;
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].start +=
                                        avgTimePerWord *
                                        numberOfWordsInLastLineOfLastEvent;
                                }
                            }
                        }

                        insertHistoryAction({
                            name: "shift line", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Move Line Down",
                description:
                    "Shift line forward to the beginning of the next Event",
                shortName: "shiftLineForward",
                keyCmd: "",

                action: async function () {
                    try {
                        if (!eventGroupState[projectState.selected]) return;
                        if (
                            eventGroupState[projectState.selected].selected
                                .length === 0
                        )
                            return;
                        if (
                            eventGroupState[projectState.selected]
                                .selected[0] ===
                            eventGroupState[projectState.selected].events
                                .length -
                                1
                        )
                            return;

                        eventGroupState[projectState.selected].selected = [
                            eventGroupState[projectState.selected].selected[0],
                        ];

                        let res = shiftLineForward(
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0]
                            ],
                            eventGroupState[projectState.selected].events[
                                eventGroupState[projectState.selected]
                                    .selected[0] + 1
                            ],
                        );

                        // Replace with plain objects so un-proxied Event class instances re-render.
                        let events = eventGroupState[projectState.selected].events;
                        let currentIndex = eventGroupState[projectState.selected].selected[0];
                        events[currentIndex] = { ...events[currentIndex], text: res.event1.text };
                        events[currentIndex + 1] = { ...events[currentIndex + 1], text: res.event2.text };

                        if (window.quillEditor) {
                            let eventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];
                            let delta = window.quillEditor.clipboard.convert({
                                html: eventGroupState[projectState.selected]
                                    .events[eventIndex].text,
                            });
                            window.quillEditor.setContents(delta, "silent");
                            window.quillEditor.formatLine(
                                0,
                                9999,
                                "align",
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].alignment === "left"
                                    ? ""
                                    : eventGroupState[projectState.selected]
                                          .events[eventIndex].alignment,
                                "silent",
                            );
                        }

                        if (editorState.updateTimeOnEdit) {
                            let currentEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0]
                                ];
                            let nextEvent =
                                eventGroupState[projectState.selected].events[
                                    eventGroupState[projectState.selected]
                                        .selected[0] + 1
                                ];

                            if (
                                currentEvent.start &&
                                currentEvent.end &&
                                nextEvent.start &&
                                nextEvent.end
                            ) {
                                let avgTimePerWord =
                                    getAvgTimePerWord(currentEvent);
                                let numberOfWordsInLastLineOfLastEvent =
                                    window.quillEditor
                                        .getText(0)
                                        .split("\n")
                                        .pop()
                                        .split(" ").length;
                                if (avgTimePerWord > 0 && avgTimePerWord !== Infinity) {
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[projectState.selected]
                                            .selected[0] + 1
                                    ].start -=
                                        avgTimePerWord *
                                        numberOfWordsInLastLineOfLastEvent;
                                    eventGroupState[
                                        projectState.selected
                                    ].events[
                                        eventGroupState[
                                            projectState.selected
                                        ].selected[0]
                                    ].end -=
                                        avgTimePerWord *
                                        numberOfWordsInLastLineOfLastEvent;
                                }
                            }
                        }

                        insertHistoryAction({
                            name: "shift line", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Split Event",
                description:
                    "Split the selected event based on the cursor location",
                shortName: "splitEvent",
                keyCmd: "",

                action: async function () {
                    try {
                        const quill = window?.quillEditor;
                        let range = quill?.getSelection();
                        let length = quill?.getLength();
                        let frameGap = 0;
                        if (editorState.minFrameGap > 0) {
                            frameGap = editorState.minFrameGap/projectState.frameRate;
                        }

                        if (
                            range &&
                            length &&
                            range.index < length - 1 &&
                            eventGroupState[projectState.selected].selected
                                .length > 0
                        ) {
                            const egIdx = projectState.selected;
                            const selectedIdx =
                                eventGroupState[egIdx].selected[0];
                            let start =
                                eventGroupState[egIdx].events[selectedIdx]
                                    .start;
                            let end =
                                eventGroupState[egIdx].events[selectedIdx]
                                    .end;

                            let duration = end - start;
                            let newEventEnd = start + duration / 2;

                            /* Split without Quill user mutations (avoids extra history). */
                            const { firstHtml, secondHtml } = splitQuillHtml(
                                quill,
                                range.index,
                            );

                            commitHistory({
                                name: "split event",
                                eventGroup: egIdx,
                                mutate: {
                                    eventGroupState: (draft) => {
                                        draft[egIdx].events[selectedIdx].text =
                                            firstHtml;
                                        draft[egIdx].events[selectedIdx].end =
                                            newEventEnd - frameGap;
                                        draft[egIdx] = insertEvent(
                                            current(draft[egIdx]),
                                            {
                                                ...$state.snapshot(
                                                    eventGroupState[egIdx]
                                                        .events[selectedIdx],
                                                ),
                                                text: secondHtml,
                                                start: newEventEnd,
                                                end: end,
                                                original: "",
                                            },
                                            selectedIdx + 1,
                                        );
                                    },
                                },
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Merge Up",
                description:
                    "Merge the selected Event with the previous Event.",
                shortName: "mergeEventUp",
                keyCmd: "",

                action: async function () {
                    try {
                        //Merge the selected event with the previous event
                        //Check that there is only one selected event in the event group and it is not the first event.
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length === 1 &&
                            eventGroupState[projectState.selected].selected[0] >
                                0
                        ) {
                            let eventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];
                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].text +=
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text;

                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].original +=
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].original;
                            if (
                                !isNaN(
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].end,
                                )
                            ) {
                                eventGroupState[projectState.selected].events[
                                    eventIndex - 1
                                ].end =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].end;
                            }
                            //Merge Tags, speakers, and metadata
                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].tags = [
                                ...new Set(
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex - 1].tags.concat(
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].tags,
                                    ),
                                ),
                            ];

                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].speakers = [
                                ...new Set(
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex - 1].speakers.concat(
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].speakers,
                                    ),
                                ),
                            ];

                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].metadata = {
                                ...eventGroupState[projectState.selected]
                                    .events[eventIndex - 1].metadata,
                                ...eventGroupState[projectState.selected]
                                    .events[eventIndex].metadata,
                            };
                            //set rendered to false
                            eventGroupState[projectState.selected].events[
                                eventIndex - 1
                            ].rendered = false;

                            //Select the previous event
                            eventGroupState[projectState.selected].selected = [
                                eventIndex - 1,
                            ];

                            //Delete the merged event
                            eventGroupState[projectState.selected] =
                                removeEvent(
                                    eventGroupState[projectState.selected],
                                    eventIndex,
                                );

                            //Save to Undo/Redo history
                            insertHistoryAction({
                                name: "merge events", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        } else {
                            //notify user we are unable to merge with previous event using toast
                            toast.push(m.shortcut_keys_merge_prev_failed_toast(), {
                                classes: ["toast-warning"],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Merge Down",
                description: "Merge the selected Event with the next Event.",
                shortName: "mergeEventDown",
                keyCmd: "",
                action: async function () {
                    try {
                        //Merge the selected event with the next event
                        //Check that there is only one selected event in the event group and it is not the last event.
                        if (
                            eventGroupState[projectState.selected] &&
                            eventGroupState[projectState.selected].selected
                                .length === 1 &&
                            eventGroupState[projectState.selected].selected[0] <
                                eventGroupState[projectState.selected].events
                                    .length -
                                    1
                        ) {
                            let eventIndex =
                                eventGroupState[projectState.selected]
                                    .selected[0];
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].text +=
                                eventGroupState[projectState.selected].events[
                                    eventIndex + 1
                                ].text;

                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].original +=
                                eventGroupState[projectState.selected].events[
                                    eventIndex + 1
                                ].original;
                            if (
                                !isNaN(
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex + 1].end,
                                )
                            ) {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].end =
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex + 1].end;
                            }
                            //Merge Tags, speakers, and metadata
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].tags = [
                                ...new Set(
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].tags.concat(
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex + 1].tags,
                                    ),
                                ),
                            ];

                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].speakers = [
                                ...new Set(
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].speakers.concat(
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex + 1].speakers,
                                    ),
                                ),
                            ];

                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].metadata = {
                                ...eventGroupState[projectState.selected]
                                    .events[eventIndex].metadata,
                                ...eventGroupState[projectState.selected]
                                    .events[eventIndex + 1].metadata,
                            };
                            //set rendered to false
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].rendered = false;

                            //Select the merged event
                            eventGroupState[projectState.selected].selected = [
                                eventIndex,
                            ];

                            //Delete the next event
                            eventGroupState[projectState.selected] =
                                removeEvent(
                                    eventGroupState[projectState.selected],
                                    eventIndex + 1,
                                );

                            if (window.quillEditor) {
                                let delta =
                                    window.quillEditor.clipboard.convert({
                                        html: eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].text,
                                    });
                                window.quillEditor.setContents(delta, "silent");
                                window.quillEditor.formatLine(
                                    0,
                                    9999,
                                    "align",
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].alignment === "left"
                                        ? ""
                                        : eventGroupState[projectState.selected]
                                              .events[eventIndex].alignment,
                                    "silent",
                                );
                                if (editorState.editing) {
                                    /* Move cursor to end of event text */
                                    window.quillEditor.setSelection(
                                        window.quillEditor.getLength(),
                                        0,
                                    );
                                } else {
                                    window.quillEditor.blur();
                                }
                            }

                            //Save to Undo/Redo history
                            insertHistoryAction({
                                name: "merge events", //action name
                                eventGroup: projectState.selected,
                                snapshots: [
                                    {
                                        store: "eventGroupState",
                                        value: JSON.stringify(eventGroupState),
                                    },
                                ],
                            });
                        } else {
                            //notify user we are unable to merge with next event using toast
                            toast.push(m.shortcut_keys_merge_next_failed_toast(), {
                                classes: ["toast-warning"],
                            });
                        }
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Chain To Next Event",
                description:
                    "Snap the Event outcode to the next Event's incode",
                shortName: "snapToStart",
                keyCmd: "",
                action: async function () {
                    try {
                        const egIdx = projectState.selected;
                        const eventGroup = eventGroupState[egIdx];
                        if (!eventGroup?.selected?.length) {
                            return;
                        }

                        const selected = [...eventGroup.selected];
                        const gapSec =
                            editorState.minFrameGap / projectState.frameRate;
                        const changed = snapSelectedEndsToNext(
                            eventGroup.events,
                            selected,
                            gapSec,
                        );

                        if (!changed) {
                            return;
                        }

                        insertHistoryAction({
                            name: "snap to end", //action name
                            eventGroup: egIdx,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err);
                        console.log(err.message);
                    }
                },
            },
            {
                name: "Chain To Previous Event",
                description:
                    "Snap the Event incode to the previous Event's outcode",
                shortName: "snapToEnd",
                keyCmd: "",
                action: async function () {
                    try {
                        const egIdx = projectState.selected;
                        const eventGroup = eventGroupState[egIdx];
                        if (!eventGroup?.selected?.length) {
                            return;
                        }

                        const selected = [...eventGroup.selected];
                        const gapSec =
                            editorState.minFrameGap / projectState.frameRate;
                        const changed = snapSelectedStartsToPrevious(
                            eventGroup.events,
                            selected,
                            gapSec,
                        );

                        if (!changed) {
                            return;
                        }

                        insertHistoryAction({
                            name: "snap to start", //action name
                            eventGroup: egIdx,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err);
                        console.log(err.message);
                    }
                },
            },
            {
                name: "Expand",
                description: "Expand the event text horizontally",
                shortName: "expand",
                keyCmd: "",

                action: async function () {
                    if (!eventGroupState[projectState.selected]) {
                        return;
                    }

                    if (
                        !eventGroupState[projectState.selected].selected ||
                        eventGroupState[projectState.selected].selected
                            .length === 0
                    ) {
                        return;
                    }

                    eventGroupState[projectState.selected].selected.forEach(
                        (eventIndex, selectedEventIndex) => {
                            if (
                                !eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text
                            ) {
                                return;
                            }

                            try {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text = expandText(
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].text,
                                    eventGroupState[projectState.selected]
                                        .maxChars,
                                );

                                if (
                                    selectedEventIndex === 0 &&
                                    window.quillEditor
                                ) {
                                    if (
                                        window.quillEditor.getSemanticHTML() !==
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].text
                                    ) {
                                        let delta =
                                            window.quillEditor.clipboard.convert(
                                                {
                                                    html: eventGroupState[
                                                        projectState.selected
                                                    ].events[eventIndex].text,
                                                },
                                            );
                                        window.quillEditor.setContents(
                                            delta,
                                            "silent",
                                        );
                                        window.quillEditor.formatLine(
                                            0,
                                            9999,
                                            "align",
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].alignment ===
                                                "left"
                                                ? ""
                                                : eventGroupState[
                                                      projectState.selected
                                                  ].events[eventIndex]
                                                      .alignment,
                                            "silent",
                                        );
                                    }
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "expand event(s)", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Compress",
                description: "Compress the event text vertically",
                shortName: "compress",
                keyCmd: "",

                action: async function () {
                    if (!eventGroupState[projectState.selected]) {
                        return;
                    }

                    if (
                        !eventGroupState[projectState.selected].selected ||
                        eventGroupState[projectState.selected].selected
                            .length === 0
                    ) {
                        return;
                    }

                    eventGroupState[projectState.selected].selected.forEach(
                        (eventIndex, selectedEventIndex) => {
                            try {
                                if (
                                    !eventGroupState[projectState.selected]
                                        .events[eventIndex].text
                                ) {
                                    return;
                                }

                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].text = compressText(
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].text,
                                    eventGroupState[projectState.selected]
                                        .maxChars,
                                    eventGroupState[projectState.selected]
                                        .maxLines,
                                );

                                if (
                                    selectedEventIndex === 0 &&
                                    window.quillEditor
                                ) {
                                    if (
                                        window.quillEditor.getSemanticHTML() !==
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].text
                                    ) {
                                        let delta =
                                            window.quillEditor.clipboard.convert(
                                                {
                                                    html: eventGroupState[
                                                        projectState.selected
                                                    ].events[eventIndex].text,
                                                },
                                            );
                                        window.quillEditor.setContents(
                                            delta,
                                            "silent",
                                        );
                                        window.quillEditor.formatLine(
                                            0,
                                            9999,
                                            "align",
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].alignment ===
                                                "left"
                                                ? ""
                                                : eventGroupState[
                                                      projectState.selected
                                                  ].events[eventIndex]
                                                      .alignment,
                                            "silent",
                                        );
                                    }
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "compress event(s)", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Auto Format",
                description:
                    "Auto format the currently selected event based on the Event Group Settings",
                shortName: "autoFormat",
                keyCmd: "",
                action: async function () {
                    if (
                        !eventGroupState[projectState.selected] ||
                        eventGroupState[projectState.selected].selected
                            .length === 0
                    ) {
                        return;
                    }

                    try {
                        eventGroupState[projectState.selected] = autoFormat(
                            eventGroupState[projectState.selected],
                            eventGroupState[projectState.selected].maxLines,
                            eventGroupState[projectState.selected].maxChars,
                            eventGroupState[projectState.selected].minDuration,
                            true,
                            eventGroupState[projectState.selected].selected,
                        );

                        if (window.quillEditor) {
                            try {
                                let eventIndex =
                                    eventGroupState[projectState.selected]
                                        .selected[0];
                                let delta =
                                    window.quillEditor.clipboard.convert({
                                        html: eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].text,
                                    });
                                window.quillEditor.setContents(delta, "silent");
                                window.quillEditor.formatLine(
                                    0,
                                    9999,
                                    "align",
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].alignment === "left"
                                        ? ""
                                        : eventGroupState[projectState.selected]
                                              .events[eventIndex].alignment,
                                    "silent",
                                );
                            } catch (err) {
                                console.log(err, err.message);
                            }
                        }

                        insertHistoryAction({
                            name: "auto format", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Order Events by Time",
                description: "Sort the Event List by start time.",
                shortName: "orderByTime",
                keyCmd: "",

                action: async function () {
                    eventGroupState[projectState.selected] = orderByTime(
                        eventGroupState[projectState.selected],
                    );

                    insertHistoryAction({
                        name: "ordering", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });

                    toast.push(m.shortcut_keys_ordered_by_time_toast(), {
                        classes: ["toast-success"],
                    });
                },
            },
            {
                name: "Clear Event Text",
                description: "Clear the text of the selected Events",
                shortName: "clearEventText",
                keyCmd: "",
                action: async function () {
                    if (
                        !eventGroupState[projectState.selected] ||
                        eventGroupState[projectState.selected].selected
                            .length === 0
                    ) {
                        return;
                    }

                    //Ask the user if they're sure they want to clear the Event Text using sweetalert
                    let result = await Swal.fire({
                        ...SWAL_BASE,
                        customClass: SWAL_CLASSES.confirmCancel,
                        title: m.shortcut_keys_clear_text_title(),
                        text: m.shortcut_keys_clear_text_text(),
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: m.shortcut_keys_clear_text_confirm(),
                        cancelButtonText: m.shortcut_keys_clear_text_cancel(),
                        reverseButtons: true,
                    });

                    if (!result.isConfirmed) {
                        return;
                    }

                    eventGroupState[projectState.selected].selected.forEach(
                        (eventIndex, order) => {
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].text = "";
                            try {
                                if (window.quillEditor && order === 0) {
                                    window.quillEditor.setText("");
                                }
                            } catch (err) {
                                console.log(err, err.message);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "clear text", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Clear Timecodes",
                description: "Clear the timecodes of the selected Events",
                shortName: "clearTimecodes",
                keyCmd: "",
                action: async function () {
                    if (
                        !eventGroupState[projectState.selected] ||
                        eventGroupState[projectState.selected].selected
                            .length === 0
                    ) {
                        return;
                    }

                    //Ask the user if they're sure they want to clear the Event Text using sweetalert
                    let result = await Swal.fire({
                        ...SWAL_BASE,
                        customClass: SWAL_CLASSES.confirmCancel,
                        title: m.shortcut_keys_clear_timecodes_title(),
                        text: m.shortcut_keys_clear_timecodes_text(),
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: m.shortcut_keys_clear_timecodes_confirm(),
                        cancelButtonText: m.shortcut_keys_clear_timecodes_cancel(),
                        reverseButtons: true,
                    });

                    if (!result.isConfirmed) {
                        return;
                    }

                    eventGroupState[projectState.selected].selected.forEach(
                        (eventIndex, order) => {
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].start = false;
                            eventGroupState[projectState.selected].events[
                                eventIndex
                            ].end = false;
                        },
                    );

                    insertHistoryAction({
                        name: "clear timecodes", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Adjust Duration to Reading Speed",
                description:
                    "Automatically update the duration of the Event based on the set reading speed (CPS) of the Event Group.",
                shortName: "adjustDuration",
                keyCmd: "",
                action: async function () {
                    if (
                        !eventGroupState[projectState.selected] ||
                        eventGroupState[projectState.selected].selected
                            .length === 0
                    ) {
                        return;
                    }

                    let cps =
                        eventGroupState[projectState.selected].maxCps || 22;
                    let minDuration =
                        eventGroupState[projectState.selected].minDuration ||
                        0.83;
                    let currentTime = player ? player.currentTime : 0;

                    eventGroupState[projectState.selected].selected
                        .sort()
                        .forEach((eventIndex) => {
                            try {
                                let event =
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex];
                                let text = convertToPlainText(event.text, "");
                                let characters = text.length;

                                if (characters === 0 || cps === 0) {
                                    return;
                                }

                                let duration = Math.max(
                                    minDuration,
                                    characters / cps,
                                );
                                if (event.start > 0 && event.end > 0) {
                                    //Check if the next event exists, and if updating the end time of the event will cause an overlap
                                    if (
                                        !eventGroupState[projectState.selected]
                                            .events[eventIndex + 1]
                                    ) {
                                        //If the next event does not exist, update the end time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    } else if (
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex + 1].start >=
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].start +
                                            duration
                                    ) {
                                        //If the next event exists and there is no overlap, update the end time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    } else if (
                                        !eventGroupState[projectState.selected]
                                            .events[eventIndex - 1] &&
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].end -
                                            duration >=
                                            0
                                    ) {
                                        //If the previous event does not exist, update the start time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    } else if (
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex - 1].end <=
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].end -
                                            duration
                                    ) {
                                        //If the previous event exists and there is no overlap, update the start time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    } else {
                                        //Just update the end of the Event and deal with the overlap.
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    }
                                } else if (event.start > 0) {
                                    if (
                                        !eventGroupState[projectState.selected]
                                            .events[eventIndex + 1]
                                    ) {
                                        //If the next event does not exist, update the end time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    } else if (
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex + 1].start >=
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].start +
                                            duration
                                    ) {
                                        //If the next event exists and there is no overlap, update the end time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    } else if (
                                        !eventGroupState[projectState.selected]
                                            .events[eventIndex - 1]
                                    ) {
                                        //If the previous event does not exist, update the start time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex + 1].start;
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    } else if (
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex - 1].end <=
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex + 1].start -
                                            duration
                                    ) {
                                        //If the previous event exists and there is no overlap, update the start time of the event
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex + 1].start;
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    } else {
                                        //Just update the end of the Event and deal with the overlap.
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    }
                                } else if (event.end > 0) {
                                    //End time exists so we base the start time on that
                                    if (
                                        !eventGroupState[projectState.selected]
                                            .events[eventIndex - 1]
                                    ) {
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    } else if (
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex - 1].end <=
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].end -
                                            duration
                                    ) {
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    } else if (
                                        !eventGroupState[projectState.selected]
                                            .events[eventIndex + 1]
                                    ) {
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex - 1].end;
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    } else if (
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex + 1].start >=
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex - 1].end +
                                            duration
                                    ) {
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex - 1].end;
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].end =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].start +
                                            duration;
                                    } else {
                                        eventGroupState[
                                            projectState.selected
                                        ].events[eventIndex].start =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex].end - duration;
                                    }
                                } else {
                                    if (
                                        currentTime === 0 &&
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex - 1] &&
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex - 1].end > 0
                                    ) {
                                        currentTime =
                                            eventGroupState[
                                                projectState.selected
                                            ].events[eventIndex - 1].end;
                                    }

                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].start = currentTime;
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].end =
                                        currentTime + duration;
                                    currentTime = currentTime + duration;
                                }
                            } catch (err) {
                                console.log(err, err.message);
                            }
                        });

                    insertHistoryAction({
                        name: "adjust duration", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
        ],
    },
    {
        name: "Timeline",
        shortcuts: [
            {
                name: "Zoom In",
                description: "Zoom in on the Timeline",
                shortName: "timelineZoomIn",
                keyCmd: "",
                action: async function () {
                    try {
                        window.timeline?.zoomIn?.();
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            {
                name: "Zoom Out",
                description: "Zoom out on the Timeline",
                shortName: "timelineZoomOut",
                keyCmd: "",
                action: async function () {
                    try {
                        window.timeline?.zoomOut?.();
                    } catch (err) {
                        console.log(err, err.message);
                    }
                },
            },
            /* Select Previous Event Closest to playhead (player.currentTime) */
            {
                name: "Select Previous Event On Timeline",
                description:
                    "Selects the previous event closest to the current player time",
                shortName: "selectPrevEvent",
                keyCmd: "", // Assign a key command if needed
                action: async function () {
                    if (!player) {
                        return;
                    }

                    //Find the closest event to the current time (player.currentTime) based on the event.end being closest to player.currentTime. There may be multiple Events that are before the current time so we need to find the closest one.
                    let closestEventIndex = -1;
                    let closestEventTime = 0;
                    eventGroupState[projectState.selected].events.forEach(
                        (event, index) => {
                            if (
                                event.end <= player.currentTime &&
                                player.currentTime - event.end <
                                    player.currentTime - closestEventTime
                            ) {
                                closestEventIndex = index;
                                closestEventTime = event.end;
                            }
                        },
                    );

                    if (closestEventIndex >= 0) {
                        eventGroupState[projectState.selected].selected = [
                            closestEventIndex,
                        ];
                        player.currentTime =
                            eventGroupState[projectState.selected].events[
                                closestEventIndex
                            ].start;
                    }
                },
            },
            /* Select the next event closest to the playhead */
            {
                name: "Select Next Event On Timeline",
                description:
                    "Selects the next event closest to the current player time",
                shortName: "selectNextEvent",
                keyCmd: "", // Assign a key command if needed
                action: async function () {
                    if (!player) {
                        return;
                    }

                    //Find the closest event to the current time (player.currentTime) based on the event.start being closest to player.currentTime. There may be multiple Events that are after the current time so we need to find the closest one.
                    let playerTime = player.currentTime.toFixed(3);

                    let closestEventIndex = eventGroupState[
                        projectState.selected
                    ].events.findIndex((ev, index) => {
                        return (
                            ev.start &&
                            ev.start > playerTime &&
                            index !==
                                eventGroupState[projectState.selected]
                                    .selected[0]
                        );
                    });

                    if (closestEventIndex >= 0) {
                        eventGroupState[projectState.selected].selected = [
                            closestEventIndex,
                        ];
                        player.currentTime =
                            eventGroupState[projectState.selected].events[
                                closestEventIndex
                            ].start;
                    }
                },
            },
        ],
    },
    {
        name: "Audio Description",
        shortcuts: [
            {
                name: "Render Text-to-Speech",
                description: "Render Audio Description (DV) text to audio.",
                shortName: "renderAudio",
                keyCmd: "",

                action: async function () {
                    try {
                        if (authState.ad || environment.adLicense) {
                            showModal("renderAudio");
                        } else {
                            throw new Error(
                                m.shortcut_keys_ad_plugin_not_activated_error(),
                            );
                        }
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_ad_render_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Trim To Audio Duration",
                description:
                    "Update the outcode of selected audio descripiton Events to match the duration of the rendered audio.",
                shortName: "trimToDuration",
                keyCmd: "",

                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                if (
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].audioFileDuration >
                                        0 &&
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].start >= 0
                                ) {
                                    eventGroupState[
                                        projectState.selected
                                    ].events[eventIndex].end =
                                        eventGroupState[projectState.selected]
                                            .events[eventIndex].start +
                                        voiceUtils.effectiveAudioDuration(
                                            eventGroupState[projectState.selected]
                                                .events[eventIndex],
                                        ) +
                                        0.2;
                                }
                            },
                        );

                        insertHistoryAction({
                            name: "trim to duration", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });

                        toast.push(m.shortcut_keys_ad_trim_success_toast(), {
                            classes: ["toast-success"],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_ad_trim_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
        ],
    },
    {
        name: "Closed Captioning",
        shortcuts: [
            {
                name: "Position Top Left",
                description: "Position Events to the top left of the video",
                shortName: "positionTopLeft",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "start";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "start";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Position Top Center",
                description: "Position Events to the top center of the video",
                shortName: "positionTopCenter",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "start";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "center";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Position Top Right",
                description: "Position Events to the top right of the video",
                shortName: "positionTopRight",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "start";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "end";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Justify Text Left",
                description: "Justify selected event text to the left",
                shortName: "justifyTextLeft",
                keyCmd: "",
                action: async function () {
                    justifySelectedEvents("left", "start");
                },
            },
            {
                name: "Justify Text Center",
                description: "Justify selected event text to the center",
                shortName: "justifyTextCenter",
                keyCmd: "",
                action: async function () {
                    justifySelectedEvents("center", "center");
                },
            },
            {
                name: "Justify Text Right",
                description: "Justify selected event text to the right",
                shortName: "justifyTextRight",
                keyCmd: "",
                action: async function () {
                    justifySelectedEvents("right", "end");
                },
            },
            {
                name: "Align Left",
                description: "Set the alignment for selected events to the left",
                shortName: "alignLeft",
                keyCmd: "",
                action: async function () {
                    alignSelectedEvents("left");
                },
            },
            {
                name: "Align Center",
                description: "Set the alignment for selected events to the center",
                shortName: "alignCenter",
                keyCmd: "",
                action: async function () {
                    alignSelectedEvents("center");
                },
            },
            {
                name: "Align Right",
                description: "Set the alignment for selected events to the right",
                shortName: "alignRight",
                keyCmd: "",
                action: async function () {
                    alignSelectedEvents("right");
                },
            },
            {
                name: "Set Color Black",
                description:
                    "Set the text color of the selected text or current line to black",
                shortName: "setColorBlack",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("black");
                },
            },
            {
                name: "Set Color White",
                description:
                    "Set the text color of the selected text or current line to white",
                shortName: "setColorWhite",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("white");
                },
            },
            {
                name: "Set Color Red",
                description:
                    "Set the text color of the selected text or current line to red",
                shortName: "setColorRed",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("red");
                },
            },
            {
                name: "Set Color Green",
                description:
                    "Set the text color of the selected text or current line to green",
                shortName: "setColorGreen",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("green");
                },
            },
            {
                name: "Set Color Yellow",
                description:
                    "Set the text color of the selected text or current line to yellow",
                shortName: "setColorYellow",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("yellow");
                },
            },
            {
                name: "Set Color Blue",
                description:
                    "Set the text color of the selected text or current line to blue",
                shortName: "setColorBlue",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("blue");
                },
            },
            {
                name: "Set Color Magenta",
                description:
                    "Set the text color of the selected text or current line to magenta",
                shortName: "setColorMagenta",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("magenta");
                },
            },
            {
                name: "Set Color Cyan",
                description:
                    "Set the text color of the selected text or current line to cyan",
                shortName: "setColorCyan",
                keyCmd: "",
                action: async function () {
                    applySelectedTextColor("cyan");
                },
            },
            /* Position Shortcuts */
            {
                name: "Position Center Left",
                description: "Position Events to the center left of the video",
                shortName: "positionCenterLeft",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "center";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "start";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Position Center",
                description: "Position Events to the center of the video",
                shortName: "positionCenter",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "center";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "center";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Position Center Right",
                description: "Position Events to the center right of the video",
                shortName: "positionCenterRight",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "center";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "end";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            /* Position Shortcuts */
            {
                name: "Position Bottom Left",
                description: "Position Events to the bottom left of the video",
                shortName: "positionBottomLeft",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "end";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "start";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Position Bottom Center",
                description:
                    "Position Events to the bottom center of the video",
                shortName: "positionBottomCenter",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "end";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "center";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Position Bottom Right",
                description: "Position Events to the bottom right of the video",
                shortName: "positionBottomRight",
                keyCmd: "",
                action: async function () {
                    try {
                        eventGroupState[projectState.selected].selected.forEach(
                            (eventIndex) => {
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].yPos = "end";
                                eventGroupState[projectState.selected].events[
                                    eventIndex
                                ].xPos = "end";
                            },
                        );

                        insertHistoryAction({
                            name: "position event(s)", //action name
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });
                    } catch (err) {
                        console.log(err, err.message);
                        toast.push(
                            m.shortcut_keys_cc_position_failed_toast({
                                error: err.message,
                            }),
                            {
                                classes: ["toast-danger"],
                            },
                        );
                    }
                },
            },
            {
                name: "Move Up 1 Line ",
                description: "Move the Event position up 1 line",
                shortName: "positionUpLine",
                keyCmd: "",
                action: async function () {
                    const displayHeight = styleState.height || 15;
                    eventGroupState[projectState.selected].selected.forEach(
                        (evIndex) => {
                            try {
                                const event =
                                    eventGroupState[projectState.selected]
                                        .events[evIndex];
                                const currentY = resolveEventYPosition(
                                    event,
                                    displayHeight,
                                );
                                event.yPos = normalizeEventYPosition(
                                    event,
                                    currentY - 1,
                                    displayHeight,
                                );
                            } catch (err) {
                                console.error("Position line up error");
                                console.error(err.message);
                                console.log(err);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "position line up", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Move Down 1 Line ",
                description: "Move the Event position down 1 line",
                shortName: "positionDownLine",
                keyCmd: "",
                action: async function () {
                    const displayHeight = styleState.height || 15;

                    eventGroupState[projectState.selected].selected.forEach(
                        (evIndex) => {
                            try {
                                const event =
                                    eventGroupState[projectState.selected]
                                        .events[evIndex];
                                const currentY = resolveEventYPosition(
                                    event,
                                    displayHeight,
                                );
                                event.yPos = normalizeEventYPosition(
                                    event,
                                    currentY + 1,
                                    displayHeight,
                                );
                            } catch (err) {
                                console.error("Position line down error");
                                console.error(err.message);
                                console.log(err);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "position line down", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Move Left 1 Character",
                description: "Move the Event position left 1 character",
                shortName: "positionLeftCharacter",
                keyCmd: "",
                action: async function () {
                    const displayWidth = styleState.width || 32;

                    eventGroupState[projectState.selected].selected.forEach(
                        (evIndex) => {
                            try {
                                const event =
                                    eventGroupState[projectState.selected]
                                        .events[evIndex];
                                const currentX = resolveEventXPosition(
                                    event,
                                    displayWidth,
                                );

                                // Move left one character (subtract 1)
                                event.xPos = normalizeEventXPosition(
                                    event,
                                    currentX - 1,
                                    displayWidth,
                                );
                            } catch (err) {
                                console.error("Position character left error");
                                console.error(err.message);
                                console.log(err);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "position character left", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
            {
                name: "Move Right 1 Character",
                description: "Move the Event position right 1 character",
                shortName: "positionRightCharacter",
                keyCmd: "",
                action: async function () {
                    const displayWidth = styleState.width || 32;

                    eventGroupState[projectState.selected].selected.forEach(
                        (evIndex) => {
                            try {
                                const event =
                                    eventGroupState[projectState.selected]
                                        .events[evIndex];
                                const currentX = resolveEventXPosition(
                                    event,
                                    displayWidth,
                                );

                                // Move right one character (add 1)
                                event.xPos = normalizeEventXPosition(
                                    event,
                                    currentX + 1,
                                    displayWidth,
                                );
                            } catch (err) {
                                console.error("Position character right error");
                                console.error(err.message);
                                console.log(err);
                            }
                        },
                    );

                    insertHistoryAction({
                        name: "position character right", //action name
                        eventGroup: projectState.selected,
                        snapshots: [
                            {
                                store: "eventGroupState",
                                value: JSON.stringify(eventGroupState),
                            },
                        ],
                    });
                },
            },
        ],
    },
]);

const shortcutGroupLabelMap = {
    System: m.shortcut_keys_group_system(),
    "Media Player": m.shortcut_keys_group_media_player(),
    Editor: m.shortcut_keys_group_editor(),
    Timeline: m.shortcut_keys_group_timeline(),
    "Audio Description": m.shortcut_keys_group_audio_description(),
    "Closed Captioning": m.shortcut_keys_group_closed_captioning(),
};

const shortcutNameLabelMap = {
    "New Project": m.shortcut_keys_name_new_project(),
    "Export File": m.shortcut_keys_name_export_file(),
    "Batch Export": m.shortcut_keys_name_batch_export(),
    "Import File": m.shortcut_keys_name_import_file(),
    "Save Project": m.shortcut_keys_name_save_project(),
    "Toggle AI Assistant": m.shortcut_keys_name_toggle_ai_assistant(),
    Copy: m.shortcut_keys_name_copy(),
    Cut: m.shortcut_keys_name_cut(),
    Paste: m.shortcut_keys_name_paste(),
    "Next Marker": m.shortcut_keys_name_next_marker(),
    "Previous Marker": m.shortcut_keys_name_previous_marker(),
    "Insert Marker": m.shortcut_keys_name_insert_marker(),
    "Insert Marker + Comment": m.shortcut_keys_name_insert_marker_comment(),
    "Toggle Playback (Play/Pause)": m.shortcut_keys_name_toggle_playback(),
    "Toggle Fast Forward": m.shortcut_keys_name_toggle_fast_forward(),
    "Toggle Rewind": m.shortcut_keys_name_toggle_rewind(),
    "Reverse 1 Frame": m.shortcut_keys_name_reverse_one_frame(),
    "Forward 1 Frame": m.shortcut_keys_name_forward_one_frame(),
    "Reverse 5 Frame": m.shortcut_keys_name_reverse_five_frames(),
    "Forward 5 Frame": m.shortcut_keys_name_forward_five_frames(),
    "Reverse 10 Frame": m.shortcut_keys_name_reverse_ten_frames(),
    "Forward 10 Frame": m.shortcut_keys_name_forward_ten_frames(),
    "Reverse 12 Frame": m.shortcut_keys_name_reverse_eleven_frames(),
    "Forward 12 Frame": m.shortcut_keys_name_forward_eleven_frames(),
    "Reverse 1 Second": m.shortcut_keys_name_reverse_one_second(),
    "Forward 1 Second": m.shortcut_keys_name_forward_one_second(),
    "Reverse 3 Second": m.shortcut_keys_name_reverse_three_seconds(),
    "Forward 3 Second": m.shortcut_keys_name_forward_three_seconds(),
    "Go To Start Of Event": m.shortcut_keys_name_go_to_event_start(),
    "Go To End Of Event": m.shortcut_keys_name_go_to_event_end(),
    "Increase Playback Speed": m.shortcut_keys_name_increase_playback_speed(),
    "Decrease Playback Speed": m.shortcut_keys_name_decrease_playback_speed(),
    "Toggle Caption Lock": m.shortcut_keys_name_toggle_caption_lock(),
    "Toggle Video Lock": m.shortcut_keys_name_toggle_video_lock(),
    "Toggle Play Preview": m.shortcut_keys_name_toggle_play_preview(),
    "Select Next Event": m.shortcut_keys_name_select_next_event(),
    "Select Previous Event": m.shortcut_keys_name_select_previous_event(),
    "Delete Event": m.shortcut_keys_name_delete_event(),
    "Insert Event (Above)": m.shortcut_keys_name_insert_event_above(),
    "Insert Event (Below)": m.shortcut_keys_name_insert_event_below(),
    "Go To Event": m.shortcut_keys_name_go_to_event(),
    "Insert Speaker": m.shortcut_keys_name_insert_speaker(),
    "Toggle Edit Mode": m.shortcut_keys_name_toggle_edit_mode(),
    "Set Incode (Start)": m.shortcut_keys_name_set_incode(),
    "Set Outcode (End)": m.shortcut_keys_name_set_outcode(),
    "Add 1 Frame To Start": m.shortcut_keys_name_add_frame_to_start(),
    "Add 1 Frame To End": m.shortcut_keys_name_add_frame_to_end(),
    "Subtract 1 Frame From Start":
        m.shortcut_keys_name_subtract_frame_from_start(),
    "Subtract 1 Frame From End":
        m.shortcut_keys_name_subtract_frame_from_end(),
    "Shift Event Backward": m.shortcut_keys_name_shift_event_backward(),
    "Shift Event Forward": m.shortcut_keys_name_shift_event_forward(),
    "Offset Timecode": m.shortcut_keys_name_offset_timecode(),
    "Toggle Forced Narrative Subtitles":
        m.shortcut_keys_name_toggle_forced_narrative_subtitles(),
    "Assign Track None": m.shortcut_keys_name_assign_track_none(),
    "Assign Track A": m.shortcut_keys_name_assign_track_a(),
    "Assign Track B": m.shortcut_keys_name_assign_track_b(),
    "Assign Track C": m.shortcut_keys_name_assign_track_c(),
    "Assign Track D": m.shortcut_keys_name_assign_track_d(),
    "Insert Ellipsis": m.shortcut_keys_name_insert_ellipsis(),
    "Insert ♪": m.shortcut_keys_name_insert_music_note(),
    "Insert Surround ♪": m.shortcut_keys_name_insert_surround_music_note(),
    "Insert Surround ♪♪":
        m.shortcut_keys_name_insert_surround_music_notes_double(),
    "Highlight Next Event": m.shortcut_keys_name_highlight_next_event(),
    "Highlight Previous Event":
        m.shortcut_keys_name_highlight_previous_event(),
    "Select All Events": m.shortcut_keys_name_select_all_events(),
    "Select All Events From Current":
        m.shortcut_keys_name_select_all_events_from_current(),
    "Move Word Up": m.shortcut_keys_name_move_word_up(),
    "Move Word Down": m.shortcut_keys_name_move_word_down(),
    "Move Word To Previous Event":
        m.shortcut_keys_name_move_word_to_previous_event(),
    "Move Word To Next Event":
        m.shortcut_keys_name_move_word_to_next_event(),
    "Move Line Up": m.shortcut_keys_name_move_line_up(),
    "Move Line Down": m.shortcut_keys_name_move_line_down(),
    "Split Event": m.shortcut_keys_name_split_event(),
    "Merge Up": m.shortcut_keys_name_merge_up(),
    "Merge Down": m.shortcut_keys_name_merge_down(),
    "Chain To Next Event": m.shortcut_keys_name_chain_to_next_event(),
    "Chain To Previous Event": m.shortcut_keys_name_chain_to_previous_event(),
    Expand: m.shortcut_keys_name_expand(),
    Compress: m.shortcut_keys_name_compress(),
    "Auto Format": m.shortcut_keys_name_auto_format(),
    "Order Events by Time": m.shortcut_keys_name_order_events_by_time(),
    "Clear Event Text": m.shortcut_keys_name_clear_event_text(),
    "Clear Timecodes": m.shortcut_keys_name_clear_timecodes(),
    "Adjust Duration to Reading Speed":
        m.shortcut_keys_name_adjust_duration_reading_speed(),
    "Zoom In": m.shortcut_keys_name_timeline_zoom_in(),
    "Zoom Out": m.shortcut_keys_name_timeline_zoom_out(),
    "Select Previous Event On Timeline":
        m.shortcut_keys_name_timeline_select_prev_event(),
    "Select Next Event On Timeline":
        m.shortcut_keys_name_timeline_select_next_event(),
    "Render Text-to-Speech": m.shortcut_keys_name_ad_render_tts(),
    "Trim To Audio Duration": m.shortcut_keys_name_ad_trim_to_duration(),
    "Position Top Left": m.shortcut_keys_name_cc_position_top_left(),
    "Position Top Center": m.shortcut_keys_name_cc_position_top_center(),
    "Position Top Right": m.shortcut_keys_name_cc_position_top_right(),
    "Position Center Left": m.shortcut_keys_name_cc_position_center_left(),
    "Position Center": m.shortcut_keys_name_cc_position_center(),
    "Position Center Right": m.shortcut_keys_name_cc_position_center_right(),
    "Position Bottom Left": m.shortcut_keys_name_cc_position_bottom_left(),
    "Position Bottom Center": m.shortcut_keys_name_cc_position_bottom_center(),
    "Position Bottom Right": m.shortcut_keys_name_cc_position_bottom_right(),
    "Move Up 1 Line ": m.shortcut_keys_name_cc_move_up_one_line(),
    "Move Down 1 Line ": m.shortcut_keys_name_cc_move_down_one_line(),
    "Move Left 1 Character": m.shortcut_keys_name_cc_move_left_one_character(),
    "Move Right 1 Character":
        m.shortcut_keys_name_cc_move_right_one_character(),
    "Set Color Black": m.shortcut_keys_name_set_color_black(),
    "Set Color White": m.shortcut_keys_name_set_color_white(),
    "Set Color Red": m.shortcut_keys_name_set_color_red(),
    "Set Color Green": m.shortcut_keys_name_set_color_green(),
    "Set Color Yellow": m.shortcut_keys_name_set_color_yellow(),
    "Set Color Blue": m.shortcut_keys_name_set_color_blue(),
    "Set Color Magenta": m.shortcut_keys_name_set_color_magenta(),
    "Set Color Cyan": m.shortcut_keys_name_set_color_cyan(),
};

const shortcutDescriptionLabelMap = {
    "Open the New Project window": m.shortcut_keys_desc_new_project(),
    "Open the export menu": m.shortcut_keys_desc_export_file(),
    "Open the batch export menu": m.shortcut_keys_desc_batch_export(),
    "Open the import file menu": m.shortcut_keys_desc_import_file(),
    "Save project or open 'Save As' dialogue box":
        m.shortcut_keys_desc_save_project(),
    "Open or close the AI Assistant panel":
        m.shortcut_keys_desc_toggle_ai_assistant(),
    "Copy event or selected text": m.shortcut_keys_desc_copy(),
    "Cut event or selected text": m.shortcut_keys_desc_cut(),
    "Paste event or text": m.shortcut_keys_desc_paste(),
    "Go to next marker": m.shortcut_keys_desc_next_marker(),
    "Go to previous marker": m.shortcut_keys_desc_previous_marker(),
    "Insert a new marker into the selected marker list at the current time":
        m.shortcut_keys_desc_insert_marker(),
    "Insert a new marker into the selected marker list at the current time and prompt for a comment":
        m.shortcut_keys_desc_insert_marker_comment(),
    "Play or pause video playback": m.shortcut_keys_desc_toggle_playback(),
    "Fast Forward through video (foot fedal recommended)":
        m.shortcut_keys_desc_toggle_fast_forward(),
    "Rewind through video (foot fedal recommended)":
        m.shortcut_keys_desc_toggle_rewind(),
    "Go back one frame in the video":
        m.shortcut_keys_desc_reverse_one_frame(),
    "Go forward one frame in the video":
        m.shortcut_keys_desc_forward_one_frame(),
    "Go back five frames in the video":
        m.shortcut_keys_desc_reverse_five_frames(),
    "Go forward five frames in the video":
        m.shortcut_keys_desc_forward_five_frames(),
    "Go back ten frames in the video":
        m.shortcut_keys_desc_reverse_ten_frames(),
    "Go forward ten frames in the video":
        m.shortcut_keys_desc_forward_ten_frames(),
    "Go back eleven frames in the video":
        m.shortcut_keys_desc_reverse_eleven_frames(),
    "Go forward eleven frames in the video":
        m.shortcut_keys_desc_forward_eleven_frames(),
    "Go back one second in the video":
        m.shortcut_keys_desc_reverse_one_second(),
    "Go forward one second in the video":
        m.shortcut_keys_desc_forward_one_second(),
    "Go back three second in the video":
        m.shortcut_keys_desc_reverse_three_seconds(),
    "Go forward three second in the video":
        m.shortcut_keys_desc_forward_three_seconds(),
    "Update playhead to the start of the selected event":
        m.shortcut_keys_desc_go_to_event_start(),
    "Update playhead to the end of the selected event":
        m.shortcut_keys_desc_go_to_event_end(),
    "Increase the playback speed of the player. Playback rates include 0.25x - 16x":
        m.shortcut_keys_desc_increase_playback_speed(),
    "Decrease the playback speed of the player. Playback rates include 0.25x - 16x":
        m.shortcut_keys_desc_decrease_playback_speed(),
    "Toggle caption lock on/off": m.shortcut_keys_desc_toggle_caption_lock(),
    "Toggle video lock on/off": m.shortcut_keys_desc_toggle_video_lock(),
    "Toggle play preview on/off": m.shortcut_keys_desc_toggle_play_preview(),
    "Select the next event in the list":
        m.shortcut_keys_desc_select_next_event(),
    "Select the previous event in the list":
        m.shortcut_keys_desc_select_previous_event(),
    "Delete selected event(s)": m.shortcut_keys_desc_delete_event(),
    "Insert event above the currently selected event.":
        m.shortcut_keys_desc_insert_event_above(),
    "Insert new event below the selected event":
        m.shortcut_keys_desc_insert_event_below(),
    "Open the Go To Event window to navigate to a specific event in the selected Event Group":
        m.shortcut_keys_desc_go_to_event(),
    "Open the Insert Speaker Name window":
        m.shortcut_keys_desc_insert_speaker(),
    "Toggling Edit Mode will focus the textarea of the selected Event. This will allow you to quickly edit the text of the Event. Disabling Edit Mode will return the focus to the Event List.":
        m.shortcut_keys_desc_toggle_edit_mode(),
    "Update the incode of the selected Event based on the current media time":
        m.shortcut_keys_desc_set_incode(),
    "Update the outcode of the selected Event based on the current media time":
        m.shortcut_keys_desc_set_outcode(),
    "Increase start time by one frame":
        m.shortcut_keys_desc_add_frame_to_start(),
    "Increase end time by one frame":
        m.shortcut_keys_desc_add_frame_to_end(),
    "Decrease start time by one frame":
        m.shortcut_keys_desc_subtract_frame_from_start(),
    "Decrease end time by one frame":
        m.shortcut_keys_desc_subtract_frame_from_end(),
    "Shift the selected event(s) backward by one frame":
        m.shortcut_keys_desc_shift_event_backward(),
    "Shift the selected event(s) forward by one frame":
        m.shortcut_keys_desc_shift_event_forward(),
    "Offset the timecode of the selected event(s) based on the playhead position":
        m.shortcut_keys_desc_offset_timecode(),
    "Mark selected event(s) as Forced Narrative Subtitles":
        m.shortcut_keys_desc_toggle_forced_narrative_subtitles(),
    "Clear the track assignment of the selected event(s)":
        m.shortcut_keys_desc_assign_track_none(),
    "Assign the selected event(s) to track A":
        m.shortcut_keys_desc_assign_track_a(),
    "Assign the selected event(s) to track B":
        m.shortcut_keys_desc_assign_track_b(),
    "Assign the selected event(s) to track C":
        m.shortcut_keys_desc_assign_track_c(),
    "Assign the selected event(s) to track D":
        m.shortcut_keys_desc_assign_track_d(),
    "Insert ellipsis": m.shortcut_keys_desc_insert_ellipsis(),
    "Insert single music note": m.shortcut_keys_desc_insert_music_note(),
    "Insert surround single music notes to all selected events":
        m.shortcut_keys_desc_insert_surround_music_note(),
    "Insert surround ♪♪ to all selected events":
        m.shortcut_keys_desc_insert_surround_music_notes_double(),
    "Add next Event to Selected Events":
        m.shortcut_keys_desc_highlight_next_event(),
    "Add previous Event to Selected Events":
        m.shortcut_keys_desc_highlight_previous_event(),
    "Select all events in event group":
        m.shortcut_keys_desc_select_all_events(),
    "Select all Events in Event Group starting the currently selected event.":
        m.shortcut_keys_desc_select_all_events_from_current(),
    "Move a single word up to the previous line":
        m.shortcut_keys_desc_move_word_up(),
    "Move a single word down to the next line":
        m.shortcut_keys_desc_move_word_down(),
    "Shift a single word back to the end of the previous Event":
        m.shortcut_keys_desc_move_word_to_previous_event(),
    "Shift a single word forward to the beginning of the next Event":
        m.shortcut_keys_desc_move_word_to_next_event(),
    "Shift line backwards to the end of the previous Event":
        m.shortcut_keys_desc_move_line_up(),
    "Shift line forward to the beginning of the next Event":
        m.shortcut_keys_desc_move_line_down(),
    "Split the selected event based on the cursor location":
        m.shortcut_keys_desc_split_event(),
    "Merge the selected Event with the previous Event.":
        m.shortcut_keys_desc_merge_up(),
    "Merge the selected Event with the next Event.":
        m.shortcut_keys_desc_merge_down(),
    "Snap the Event outcode to the next Event's incode":
        m.shortcut_keys_desc_chain_to_next_event(),
    "Snap the Event incode to the previous Event's outcode":
        m.shortcut_keys_desc_chain_to_previous_event(),
    "Expand the event text horizontally":
        m.shortcut_keys_desc_expand(),
    "Compress the event text vertically":
        m.shortcut_keys_desc_compress(),
    "Auto format the currently selected event based on the Event Group Settings":
        m.shortcut_keys_desc_auto_format(),
    "Sort the Event List by start time.":
        m.shortcut_keys_desc_order_events_by_time(),
    "Clear the text of the selected Events":
        m.shortcut_keys_desc_clear_event_text(),
    "Clear the timecodes of the selected Events":
        m.shortcut_keys_desc_clear_timecodes(),
    "Automatically update the duration of the Event based on the set reading speed (CPS) of the Event Group.":
        m.shortcut_keys_desc_adjust_duration_reading_speed(),
    "Zoom in on the Timeline": m.shortcut_keys_desc_timeline_zoom_in(),
    "Zoom out on the Timeline": m.shortcut_keys_desc_timeline_zoom_out(),
    "Selects the previous event closest to the current player time":
        m.shortcut_keys_desc_timeline_select_prev_event(),
    "Selects the next event closest to the current player time":
        m.shortcut_keys_desc_timeline_select_next_event(),
    "Render Audio Description (DV) text to audio.":
        m.shortcut_keys_desc_ad_render_tts(),
    "Update the outcode of selected audio descripiton Events to match the duration of the rendered audio.":
        m.shortcut_keys_desc_ad_trim_to_duration(),
    "Position Events to the top left of the video":
        m.shortcut_keys_desc_cc_position_top_left(),
    "Position Events to the top center of the video":
        m.shortcut_keys_desc_cc_position_top_center(),
    "Position Events to the top right of the video":
        m.shortcut_keys_desc_cc_position_top_right(),
    "Position Events to the center left of the video":
        m.shortcut_keys_desc_cc_position_center_left(),
    "Position Events to the center of the video":
        m.shortcut_keys_desc_cc_position_center(),
    "Position Events to the center right of the video":
        m.shortcut_keys_desc_cc_position_center_right(),
    "Position Events to the bottom left of the video":
        m.shortcut_keys_desc_cc_position_bottom_left(),
    "Position Events to the bottom center of the video":
        m.shortcut_keys_desc_cc_position_bottom_center(),
    "Position Events to the bottom right of the video":
        m.shortcut_keys_desc_cc_position_bottom_right(),
    "Move the Event position up 1 line":
        m.shortcut_keys_desc_cc_move_up_one_line(),
    "Move the Event position down 1 line":
        m.shortcut_keys_desc_cc_move_down_one_line(),
    "Move the Event position left 1 character":
        m.shortcut_keys_desc_cc_move_left_one_character(),
    "Move the Event position right 1 character":
        m.shortcut_keys_desc_cc_move_right_one_character(),
    "Set the text color of the selected text or current line to black":
        m.shortcut_keys_desc_set_color_black(),
    "Set the text color of the selected text or current line to white":
        m.shortcut_keys_desc_set_color_white(),
    "Set the text color of the selected text or current line to red":
        m.shortcut_keys_desc_set_color_red(),
    "Set the text color of the selected text or current line to green":
        m.shortcut_keys_desc_set_color_green(),
    "Set the text color of the selected text or current line to yellow":
        m.shortcut_keys_desc_set_color_yellow(),
    "Set the text color of the selected text or current line to blue":
        m.shortcut_keys_desc_set_color_blue(),
    "Set the text color of the selected text or current line to magenta":
        m.shortcut_keys_desc_set_color_magenta(),
    "Set the text color of the selected text or current line to cyan":
        m.shortcut_keys_desc_set_color_cyan(),
};

function getShortcutGroupLabel(groupName) {
    return shortcutGroupLabelMap[groupName] || groupName;
}

function getShortcutNameLabel(name) {
    return shortcutNameLabelMap[name] || name;
}

function getShortcutDescriptionLabel(description) {
    return shortcutDescriptionLabelMap[description] || description;
}

async function updateShortcut(e) {
    e.preventDefault();
    e.stopPropagation();

    /* Ignore key captures while a conflict dialog is open */
    if (Swal.isVisible()) {
        return;
    }

    if ([16, 17, 18, 91, 93, 224].indexOf(e.keyCode) > -1) {
        return;
    }

    let keyCmd = [];
    if (e.shiftKey) {
        keyCmd.push("Shift");
    }

    if (e.ctrlKey) {
        keyCmd.push("Ctrl");
    }

    if (e.altKey) {
        keyCmd.push("Alt");
    }

    if (e.metaKey) {
        keyCmd.push("Meta(CMD)");
    }

    keyCmd.push(e.code || e.key);
    keyCmd = keyCmd.join("+");

    let conflictingShortcut = null;
    shortcutGroups.forEach((group) => {
        group.shortcuts.forEach((shortcut) => {
            if (
                shortcut.keyCmd === keyCmd &&
                shortcut !== selectedShortcut
            ) {
                conflictingShortcut = shortcut;
            }
        });
    });

    if (conflictingShortcut) {
        let swalRes = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.shortcut_keys_conflict_title(),
            text: m.shortcut_keys_conflict_text({
                keyCmd: keyCmd,
                shortcutName: getShortcutNameLabel(conflictingShortcut.name),
            }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.shortcut_keys_conflict_confirm(),
            cancelButtonText: m.shortcut_keys_conflict_cancel(),
        });

        if (!swalRes.isConfirmed) {
            /* Leave the previous shortcut unchanged and do not apply the new key command */
            return;
        }

        conflictingShortcut.keyCmd = "";
    }

    selectedShortcut.keyCmd = keyCmd;
    /* Save update to local storage so we can load them each time the application starts*/
    saveToLocalStorage();
}

function saveToLocalStorage() {
    const result = saveKeyboardShortcuts(shortcutGroups);

    if (!result.success) {
        toast.push(
            m.shortcut_keys_save_failed_toast({
                error: result.error || "Unknown error",
            }),
            {
                classes: ["toast-danger"],
            },
        );
    }
}

function removeShortcut() {
    selectedShortcut.keyCmd = "";
    saveToLocalStorage();
    // shortcutGroups = shortcutGroups; // Removed - unnecessary in Svelte 5
}

function findShortcut(shortcutName, shortcutGroupList) {
    let shortcut, foundShortcut;
    shortcutGroupList.forEach((groupItem) => {
        shortcut = groupItem.shortcuts.find((shortcutItem) => {
            return shortcutItem.name === shortcutName;
        });

        if (shortcut) {
            foundShortcut = shortcut;
        }
    });

    return (
        foundShortcut || {
            keyCmd: "",
        }
    );
}

function applyShortcutKeyCommands(savedShortcuts) {
    let normalizedShortcuts = normalizeKeyboardShortcuts(savedShortcuts);

    shortcutGroups.forEach((group) => {
        group.shortcuts.forEach((shortcut) => {
            shortcut.keyCmd = findShortcut(
                shortcut.name,
                normalizedShortcuts,
            ).keyCmd;
        });
    });
}

// Shortcuts whose action is safe to run even when the selected Event Group
// is locked (read-only navigation, playback, and non-content-mutating system
// actions). Cut/Paste are handled separately below since they are only safe
// when targeting the Notes/Reply fields rather than the event text itself.
const LOCK_ALLOWED_SHORTCUT_NAMES = new Set([
    // System
    "Export File",
    "Batch Export",
    "Save Project",
    "Copy",
    "Toggle AI Assistant",
    // Editor navigation
    "Select Next Event",
    "Select Previous Event",
    "Go To First Event",
    "Go To Last Event",
    "Go To Event",
    "Toggle Edit Mode",
    // Media Player (entire group: playback/nav/marker/lock toggles)
    "Insert Marker",
    "Insert Marker + Comment",
    "Next Marker",
    "Previous Marker",
    "Toggle Playback (Play/Pause)",
    "Toggle Fast Forward",
    "Toggle Rewind",
    "Reverse 1 Frame",
    "Forward 1 Frame",
    "Reverse 5 Frame",
    "Forward 5 Frame",
    "Reverse 10 Frame",
    "Forward 10 Frame",
    "Reverse 12 Frame",
    "Forward 12 Frame",
    "Reverse 1 Second",
    "Forward 1 Second",
    "Reverse 3 Second",
    "Forward 3 Second",
    "Go To Start Of Event",
    "Go To End Of Event",
    "Increase Playback Speed",
    "Decrease Playback Speed",
    "Toggle Caption Lock",
    "Toggle Video Lock",
    "Toggle Play Preview",
    // Metadata (review fields stay editable on locked groups)
    "Toggle Forced Narrative Subtitles",
]);

// Shortcuts that edit event metadata; they are disabled when the IFrame
// parent turns that kind of metadata off.
const METADATA_SHORTCUT_KINDS = {
    "Toggle Forced Narrative Subtitles": "forced",
    "Insert Speaker": "speakers",
};

const NATIVE_CLIPBOARD_SHORTCUT_NAMES = new Set(["Copy", "Cut", "Paste"]);

// Cut/Paste are allowed while locked only when the user is targeting the
// Notes or Reply fields, since those aren't part of the locked event text.
const NOTES_OR_REPLY_ELEMENT_IDS = new Set([
    "notes-input",
    "reply-input",
    "NotesTextArea",
    "ReplyTextArea",
    "textareanotesinputlabel",
    "textareareplyinputlabel",
]);

function isNotesOrReplyFocused() {
    const activeElement = document.activeElement;
    return Boolean(activeElement && NOTES_OR_REPLY_ELEMENT_IDS.has(activeElement.id));
}

function isShortcutAllowedWhenLocked(shortcutName) {
    if (LOCK_ALLOWED_SHORTCUT_NAMES.has(shortcutName)) {
        return true;
    }

    if (shortcutName === "Cut" || shortcutName === "Paste") {
        return isNotesOrReplyFocused();
    }

    return false;
}

let lockedToastTimer;
function notifyEventGroupLocked() {
    if (lockedToastTimer) {
        return;
    }

    toast.push(m.event_group_locked_toast(), {
        classes: ["toast-warning"],
    });

    lockedToastTimer = setTimeout(() => {
        lockedToastTimer = undefined;
    }, 2000);
}

async function runShortcut(e) {
    if (Swal.isVisible()) {
        return;
    }

    if (!modalState.selected) {
        let keyCmd = [];
        if (e.shiftKey) {
            ``;
            keyCmd.push("Shift");
        }

        if (e.ctrlKey) {
            keyCmd.push("Ctrl");
        }

        if (e.altKey) {
            keyCmd.push("Alt");
        }

        if (e.metaKey) {
            keyCmd.push("Meta(CMD)");
        }

        keyCmd.push(e.code || e.key);
        keyCmd = keyCmd.join("+");
        if (keyCmd) {
            if (keyCmd === "ArrowUp" || keyCmd === "ArrowDown") {
                if (isInputFocused()) {
                    return;
                }

                if (isContentEditableFocused()) {
                    if (
                        keyCmd === "ArrowUp" &&
                        window.quillEditor &&
                        window.quillEditor.getSelection().index !== 0
                    ) {
                        return;
                    } else if (
                        keyCmd === "ArrowDown" &&
                        window.quillEditor &&
                        window.quillEditor.getSelection().index + 1 <
                            window.quillEditor.getLength()
                    ) {
                        return;
                    }
                }
            }

            let shortcutFound = shortcutGroups.find((group) => {
                group.shortcuts.forEach((shortcut) => {
                    if (shortcut.keyCmd === keyCmd) {
                        if (
                            isInputFocused() &&
                            NATIVE_CLIPBOARD_SHORTCUT_NAMES.has(shortcut.name)
                        ) {
                            return;
                        }

                        e.preventDefault();
                        e.stopPropagation();

                        const metadataKind = METADATA_SHORTCUT_KINDS[shortcut.name];
                        if (metadataKind && !canEditMetadata(metadataKind)) {
                            return true;
                        }

                        if (
                            isSelectedEventGroupLocked() &&
                            !isShortcutAllowedWhenLocked(shortcut.name)
                        ) {
                            notifyEventGroupLocked();
                            return true;
                        }

                        try {
                            shortcut.action();
                        } catch (err) {
                            console.log("---Shortcut Error---");
                            console.log(err);
                        }

                        return true;
                    }
                });
            });

            //Set up shortcuts for spacebar, and jkl keys
            if (!shortcutFound) {
                if (
                    keyCmd === "KeyJ" &&
                    !isInputFocused() &&
                    !isContentEditableFocused()
                ) {
                    //Reverse 1 frame;
                    e.preventDefault();
                    e.stopPropagation();
                    player.currentTime =
                        playerState.time - 1 / projectState.frameRate;
                    audioScrub();
                } else if (
                    (keyCmd === "KeyK" || keyCmd === "Space") &&
                    !isInputFocused() &&
                    !isContentEditableFocused()
                ) {
                    //Play/Pause
                    if (player.state.playing) {
                        e.preventDefault();
                        e.stopPropagation();
                        player.pause();
                    } else {
                        await player.play();
                    }
                } else if (
                    keyCmd === "KeyL" &&
                    !isInputFocused() &&
                    !isContentEditableFocused()
                ) {
                    //Forward 1 frame
                    e.preventDefault();
                    e.stopPropagation();
                    player.currentTime =
                        playerState.time + 1 / projectState.frameRate;
                    audioScrub();
                } else if (environment.mac) {
                    if (keyCmd === "Meta(CMD)+KeyF") {
                        e.preventDefault();
                        e.stopPropagation();

                        if (isSelectedEventGroupLocked()) {
                            notifyEventGroupLocked();
                        } else {
                            uiState.quickTools.selected = "findAndReplace";
                            projectState.mode = "edit";

                            setTimeout(() => {
                                try {
                                    document.getElementById("FindInput").focus();
                                } catch (err) {
                                    console.log(err.message);
                                }
                            }, 50);
                        }
                    } else if (
                        (keyCmd === "Meta(CMD)+KeyZ" ||
                            keyCmd === "Meta(CMD)+Shift+KeyZ") &&
                        isTextFieldFocused()
                    ) {
                        // Native undo inside the field (notes, timecode, search).
                        return;
                    } else if (keyCmd === "Meta(CMD)+KeyZ") {
                        e.preventDefault();
                        e.stopPropagation();

                        if (isUndoBlockedByLock()) {
                            notifyEventGroupLocked();
                        } else {
                            undoHistory();
                        }
                    } else if (keyCmd === "Meta(CMD)+Shift+KeyZ") {
                        e.preventDefault();
                        e.stopPropagation();

                        if (isRedoBlockedByLock()) {
                            notifyEventGroupLocked();
                        } else {
                            redoHistory();
                        }
                    }
                } else if (environment.windows || environment.linux) {
                    if (keyCmd === "Ctrl+KeyF") {
                        e.preventDefault();
                        e.stopPropagation();

                        if (isSelectedEventGroupLocked()) {
                            notifyEventGroupLocked();
                        } else {
                            uiState.quickTools.selected = "findAndReplace";
                            projectState.mode = "edit";
                            setTimeout(() => {
                                try {
                                    document.getElementById("FindInput").focus();
                                } catch (err) {
                                    console.log(err.message);
                                }
                            }, 50);
                        }
                    } else if (
                        (keyCmd === "Ctrl+KeyZ" ||
                            keyCmd === "Ctrl+Shift+KeyZ" ||
                            keyCmd === "Ctrl+KeyY") &&
                        isTextFieldFocused()
                    ) {
                        // Native undo inside the field (notes, timecode, search).
                        return;
                    } else if (keyCmd === "Ctrl+KeyZ") {
                        e.preventDefault();
                        e.stopPropagation();

                        if (isUndoBlockedByLock()) {
                            notifyEventGroupLocked();
                        } else {
                            undoHistory();
                        }
                    } else if (
                        keyCmd === "Ctrl+Shift+KeyZ" ||
                        keyCmd === "Ctrl+KeyY"
                    ) {
                        e.preventDefault();
                        e.stopPropagation();

                        if (isRedoBlockedByLock()) {
                            notifyEventGroupLocked();
                        } else {
                            redoHistory();
                        }
                    }
                }
            }
        }
    }
}

function isInputFocused() {
    return (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA" ||
        document.activeElement.tagName === "SELECT"
    );
}

const NON_TEXT_INPUT_TYPES = new Set([
    "button",
    "checkbox",
    "color",
    "file",
    "hidden",
    "image",
    "radio",
    "range",
    "reset",
    "submit",
]);

function isTextFieldFocused() {
    const el = document.activeElement;
    if (!el) {
        return false;
    }
    if (el.tagName === "TEXTAREA") {
        return !el.readOnly;
    }
    if (el.tagName === "INPUT") {
        return !el.readOnly && !NON_TEXT_INPUT_TYPES.has((el.type || "text").toLowerCase());
    }
    return false;
}

function isContentEditableFocused() {
    return document.activeElement.isContentEditable;
}

function exportShortcuts() {
    let fileBlob = new Blob([JSON.stringify(shortcutGroups)], {
        type: "text/plain;charset=utf-8",
    });
    saveAs(fileBlob, "shortcuts.json");
}

function importShortcuts() {
    if (files && files[0]) {
        let reader = new FileReader();
        reader.onload = function (e) {
            try {
                let shortcutJson = JSON.parse(e.target.result);
                applyShortcutKeyCommands(shortcutJson);

                toast.push(m.shortcut_keys_import_success_toast(), {
                    classes: ["toast-success"],
                });
                const saveResult = saveKeyboardShortcuts(shortcutGroups);
                if (!saveResult.success) {
                    toast.push(
                        m.shortcut_keys_save_failed_toast({
                            error: saveResult.error || "Unknown error",
                        }),
                        {
                            classes: ["toast-danger"],
                        },
                    );
                }
            } catch (err) {
                console.log(err, err.message);
                toast.push(
                    m.shortcut_keys_import_failed_toast({
                        error: err.message,
                    }),
                    {
                        classes: ["toast-danger"],
                    },
                );
            }
        };
        reader.readAsText(files[0]);
    }
}

function handleKeyboardShortcutsUpdated(e) {
    try {
        applyShortcutKeyCommands(
            e.detail?.shortcuts || getStoredKeyboardShortcuts(environment.mac),
        );
    } catch (err) {
        console.error("Failed to refresh keyboard shortcuts:", err);
    }
}

/* Create Event Listener */
onMount(() => {
    /* Initialize Shortcuts */
    try {
        applyShortcutKeyCommands(getStoredKeyboardShortcuts(environment.mac));
    } catch (err) {
        console.error("Failed to refresh keyboard shortcuts:", err);
    }

    //Order shortcuts in each shortcut group by shortcut.name
    shortcutGroups.forEach((group) => {
        group.shortcuts.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    });

    window.removeEventListener("keydown", runShortcut);
    window.addEventListener("keydown", runShortcut);
    window.removeEventListener(
        KEYBOARD_SHORTCUTS_UPDATED_EVENT,
        handleKeyboardShortcutsUpdated,
    );
    window.addEventListener(
        KEYBOARD_SHORTCUTS_UPDATED_EVENT,
        handleKeyboardShortcutsUpdated,
    );

    return () => {
        window.removeEventListener("keydown", runShortcut);
        window.removeEventListener(
            KEYBOARD_SHORTCUTS_UPDATED_EVENT,
            handleKeyboardShortcutsUpdated,
        );
    };
});
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'shortcutKeys' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="ShortcutKeysModal">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{m.shortcut_keys_title()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.shortcut_keys_close_aria_label()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <div class="row">
                    <div class="col">
                        <form>
                            <div class="mb-3">
                                <label
                                    class="form-label"
                                    for="ShortcutListSelect">{m.shortcut_keys_list_label()}</label>
                                <select
                                    id="ShortcutListSelect"
                                    class="form-select"
                                    size="20"
                                    bind:value={selectedShortcut}
                                    onchange={() =>
                                        document
                                            .getElementById("KeyboardInput")
                                            ?.focus()}>
                                    {#each shortcutGroups as shortcutGroup (shortcutGroup.name)}
                                        <optgroup label={getShortcutGroupLabel(shortcutGroup.name)}>
                                            {#each shortcutGroup.shortcuts as shortcutOption (shortcutOption.name)}
                                                <option value={shortcutOption}
                                                    >{getShortcutNameLabel(shortcutOption.name)}{shortcutOption.keyCmd
                                                        ? " - " +
                                                          shortcutOption.keyCmd
                                                        : ""}</option>
                                            {/each}
                                        </optgroup>
                                    {/each}
                                </select>
                            </div>
                        </form>
                        <div class="float-end">
                            <button
                                type="button"
                                class="btn btn-light"
                                onclick={exportShortcuts}
                                ><i class="bi bi-download"></i> {m.shortcut_keys_export_button()}</button>
                            <label
                                class="form-label custom-file-upload"
                                for="file-upload">
                                <i class="bi bi-upload"></i> {m.shortcut_keys_import_label()}
                            </label>
                            <input
                                id="file-upload"
                                type="file"
                                accept=".json"
                                bind:files
                                onchange={importShortcuts} />
                        </div>
                    </div>
                    <div class="col">
                        <h5>{m.shortcut_keys_description_heading()}</h5>
                        {#if selectedShortcut.description}
                            <p>{getShortcutDescriptionLabel(selectedShortcut.description)}</p>
                            <label class="form-label" for="Shortcut Key Command"
                                >{m.shortcut_keys_key_command_label()}</label>
                            <form
                                class="d-flex flex-row align-items-center flex-wrap">
                                <div class="input-group">
                                    <input
                                        id="KeyboardInput"
                                        class="form-control"
                                        type="text"
                                        onkeydown={updateShortcut}
                                        onfocus={(e) => {
                                            e.target.select();
                                        }}
                                        value={selectedShortcut.keyCmd || ""} />
                                    <button
                                        aria-label={m.shortcut_keys_clear_aria_label()}
                                        class="btn btn-danger text-white"
                                        type="button"
                                        title={m.shortcut_keys_clear_title()}
                                        onclick={removeShortcut}>
                                        <i class="bi bi-x-lg"></i>
                                    </button>
                                </div>
                                {#if selectedShortcut.keyCmd}
                                    <h5 class="mt-3">
                                        {#each selectedShortcut.keyCmd.split("+") as key, index (`${key}-${index}`)}
                                            {index > 0 ? "+" : ""}
                                            <span
                                                class="badge text-bg-secondary me-1"
                                                >{key}</span>
                                        {/each}
                                    </h5>
                                {/if}
                            </form>
                        {:else}
                            <p>
                                {m.shortcut_keys_select_prompt()}
                            </p>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
input[type="file"] {
    display: none;
}

.custom-file-upload {
    cursor: pointer;
    color: #fff;
    background-color: #444;
    border-color: #444;
    display: inline-block;
    font-weight: 400;
    text-align: center;
    vertical-align: middle;
    user-select: none;
    border: 1px solid transparent;
    padding: 0.375rem 0.75rem;
    font-size: 0.9375rem;
    line-height: 1.5;
    border-radius: 0.25rem;
    transition:
        color 0.15s ease-in-out,
        background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
    margin: 0;
}

.custom-file-upload:hover {
    color: #fff;
    background-color: #313131;
    border-color: #2b2b2b;
    text-decoration: none;
}
</style>
